import MainStore, { uniquePrimitives } from "./MainStore"
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { ArrowDownLeftIcon, ArrowUpTrayIcon, ArrowsPointingInIcon, ClipboardDocumentIcon, DocumentArrowDownIcon, FunnelIcon, MagnifyingGlassIcon, PlusIcon, SparklesIcon, TrashIcon } from '@heroicons/react/24/outline';
import { HeroIcon } from './HeroIcon';
import { InputPopup } from './InputPopup';
import DropdownButton from "./DropdownButton";
import InfiniteCanvas from "./InfiniteCanvas";
import CollectionUtils from "./CollectionHelper";
import { RenderPrimitiveAsKonva, RenderSetAsKonva, renderDatatable, renderMatrix, renderPlaceholder, renderPlainObject, themes } from "./RenderHelpers";
import HierarchyNavigator from "./HierarchyNavigator";
import PrimitiveConfig from "./PrimitiveConfig";
import FilterPane from "./FilterPane";
import CollectionInfoPane from "./CollectionInfoPane";
import useDataEvent from "./CustomHook";
import { createPptx, exportKonvaToPptx, writePptx } from "./PptHelper";
import Konva from "konva";
import { expandStringLiterals, formatNumber } from "./SharedTransforms";
import { prepareStructuredSummary, resolveSummaryStructureTemplate } from "./utils/structuredSummary";
import { getLogger } from './logger'
import AgentChat from "./AgentChat";
import clsx from "clsx";
import { Tab, Tabs } from "@heroui/react";
import { ConvertVisualizationSpec } from "./VisualizationToDataTable";
import { resolveFlowInstanceSelection } from "./utils/viewRendering";
import PrimitiveDrawer from "./PrimitiveDrawer";
import { resolvePrimitiveImageUrl } from "./utils/primitiveImage";

const log = getLogger('BoardViewer', { level: 'debug' })

                
export const IGNORE_NODES_FOR_EXPORT = ["frame_outline", "frame_bg", "item_info", "widget", "frame_label", "view", "pin", "pin_label", "plainId", "indicators", "viz_block"]
const RENDERSUB = false//true

const USE_LEGACY_ROUTING = true;
const IMAGE_FIELD_PATTERN = /(image|photo|thumbnail|logo|icon|picture|screenshot|banner|cover)/i;
const IMAGE_URL_PATTERN = /\.(png|jpe?g|gif|bmp|webp|svg|heic|heif|avif)(\?|#|$)/i;
const DATA_IMAGE_PREFIX = /^data:image\//i;
const isHeightDebugEnabled = () => (typeof window !== "undefined" && window.__DEBUG_TIMELINE_HEIGHT === true);

function toInteger(value, fallback = 0) {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === "string" && value.trim() !== "") {
        const parsed = parseInt(value, 10);
        if (!Number.isNaN(parsed)) {
            return parsed;
        }
    }
    return fallback;
}

function parseReferenceIdList(value) {
    if (!value) {
        return [];
    }
    return [value]
        .flat()
        .filter((entry) => entry !== undefined && entry !== null)
        .map((entry) => {
            if (typeof entry === "number") {
                return entry;
            }
            if (typeof entry === "string" && entry.trim() !== "") {
                const parsed = parseInt(entry, 10);
                if (!Number.isNaN(parsed)) {
                    return parsed;
                }
            }
            return undefined;
        })
        .filter((entry) => entry !== undefined);
}

function normalizeImageUrl(url) {
    if (typeof url !== "string") {
        return undefined;
    }
    const trimmed = url.trim();
    if (!trimmed) {
        return undefined;
    }
    if (trimmed.startsWith("/api/") || DATA_IMAGE_PREFIX.test(trimmed)) {
        return trimmed;
    }
    if (trimmed.startsWith("//")) {
        return `/api/remoteImage?url=${encodeURIComponent(`https:${trimmed}`)}`;
    }
    if (/^https?:\/\//i.test(trimmed)) {
        return `/api/remoteImage?url=${encodeURIComponent(trimmed)}`;
    }
    return trimmed;
}

function looksLikeImageUrl(value, keyPath = "") {
    if (typeof value !== "string") {
        return false;
    }
    const trimmed = value.trim();
    if (!trimmed) {
        return false;
    }
    if (DATA_IMAGE_PREFIX.test(trimmed)) {
        return true;
    }
    if (trimmed.startsWith("/api/image/") || trimmed.startsWith("/api/remoteImage") || trimmed.startsWith("/api/companyLogo")) {
        return true;
    }
    if (IMAGE_URL_PATTERN.test(trimmed.toLowerCase())) {
        return true;
    }
    if ((/^https?:\/\//i.test(trimmed) || trimmed.startsWith("//")) && IMAGE_FIELD_PATTERN.test(keyPath)) {
        return true;
    }
    return false;
}

function collectPrimitiveImageUrls(primitive) {
    return [resolvePrimitiveImageUrl(primitive)]
    return [
        primitive.referenceParameters.image,
        primitive.referenceParameters.imageUrl,
        primitive.referenceParameters.main_image,
        primitive.referenceParameters.profile_image_link,
        (primitive.imageCount || primitive.referenceParameters?.hasImage) ? `/api/image/${primitive.id}${primitive.imageCount ? `?${primitive.imageCount}` : ""}` : undefined
    ].filter(Boolean)
}

function expandInspectableSourceList(sourceList = []) {
    const normalized = uniquePrimitives(
        (Array.isArray(sourceList) ? sourceList : []).filter((item) => item && typeof item === "object" && item.id)
    );
    const visited = new Set();
    const leaves = [];

    const walk = (item, depth = 0) => {
        if (!item || typeof item !== "object") {
            return;
        }
        const id = item.id;
        if (!id || visited.has(id) || depth > 24) {
            return;
        }
        visited.add(id);

        const upstream = uniquePrimitives([
            ...(item?.primitives?.source?.allItems ?? []),
            ...(item?.primitives?.ref?.allItems ?? []),
        ].filter((entry) => entry && typeof entry === "object" && entry.id));

        if (upstream.length === 0) {
            leaves.push(item);
            return;
        }
        upstream.forEach((entry) => walk(entry, depth + 1));
    };

    normalized.forEach((item) => walk(item, 0));
    return uniquePrimitives(leaves.length > 0 ? leaves : normalized);
}

function expandInspectableSourceListFirstLevel(sourceList = []) {
    const normalized = uniquePrimitives(
        (Array.isArray(sourceList) ? sourceList : []).filter((item) => item && typeof item === "object" && item.id)
    );
    if (normalized.length === 0) {
        return [];
    }

    const visited = new Set(normalized.map((item) => item.id));
    let frontier = normalized;
    let depth = 0;
    const MAX_DEPTH = 24;

    while (frontier.length > 0 && depth < MAX_DEPTH) {
        const resultAtDepth = frontier.filter((item) => item?.type === "result");
        if (resultAtDepth.length > 0) {
            return uniquePrimitives(resultAtDepth);
        }

        const nextFrontier = uniquePrimitives(
            frontier.flatMap((item) => [
                ...(item?.primitives?.source?.allItems ?? []),
                ...(item?.primitives?.ref?.allItems ?? []),
            ])
            .filter((entry) => entry && typeof entry === "object" && entry.id && !visited.has(entry.id))
        );

        nextFrontier.forEach((item) => visited.add(item.id));
        frontier = nextFrontier;
        depth += 1;
    }

    // Fallback: preserve previous behavior if no result primitives exist in chain.
    return uniquePrimitives(
        normalized.flatMap((item) => {
            const upstream = uniquePrimitives([
                ...(item?.primitives?.source?.allItems ?? []),
                ...(item?.primitives?.ref?.allItems ?? []),
            ].filter((entry) => entry && typeof entry === "object" && entry.id));
            return upstream.length > 0 ? upstream : [item];
        })
    );
}

function resolveImageExpansionOptions(mode) {
    const normalized = typeof mode === "string" ? mode.toLowerCase() : undefined;
    switch (normalized) {
        case "scale":
        case "fit":
        case "contain":
            return { fit: "contain", expansionMode: "scale" };
        case "center":
        case "normal":
            return { fit: "center", expansionMode: "center" };
        case "zoom":
        case "fill":
        case "cover":
            return { fit: "cover", expansionMode: "zoom" };
        default:
            return { fit: "cover", expansionMode: "zoom" };
    }
}

function buildImageGalleryFromInputs(inputs = [], config = {}, overrides = {}) {
    const basePrimitives = uniquePrimitives(inputs.filter(Boolean));
    const limit = Math.max(1, Math.min(50, toInteger(config.image_limit ?? config.imageLimit ?? config.count, 6)));
    const descend = Boolean(config.image_descend ?? config.imageDescend ?? config.descend);
    const referenceIds = parseReferenceIdList(config.image_referenceIds ?? config.imageReferenceIds ?? config.referenceIds ?? []);
    const skipSourceIds = new Set(
        [overrides?.skipSourceIds]
            .flat()
            .map((value) => (value === undefined || value === null ? undefined : `${value}`.trim()))
            .filter(Boolean)
    );
    const filterSet = new Set(referenceIds);
    const seenCandidateIds = new Set();
    const candidates = [];
    const shouldInclude = (primitive) => filterSet.size === 0 || filterSet.has(primitive.referenceId);
    const pushCandidate = (primitive, isDescendant) => {
        if (!primitive || seenCandidateIds.has(primitive.id)) {
            return;
        }
        if (!shouldInclude(primitive)) {
            return;
        }
        seenCandidateIds.add(primitive.id);
        candidates.push({ primitive, isDescendant });
    };
    basePrimitives.forEach((primitive) => pushCandidate(primitive, false));
    if (descend) {
        basePrimitives.forEach((primitive) => {
            const descendants = primitive?.primitives?.descendants ?? [];
            uniquePrimitives(descendants).forEach((descendant) => {
                if (!descendant) {
                    return;
                }
                pushCandidate(descendant, true);
            });
        });
    }
    const seenUrls = new Set();
    const sourceIdsSet = new Set();
    const imageEntries = [];
    for (const { primitive } of candidates) {
        const urls = collectPrimitiveImageUrls(primitive);
        for (const rawUrl of urls) {
            const normalized = normalizeImageUrl(rawUrl);
            if (!normalized || seenUrls.has(normalized)) {
                continue;
            }
            seenUrls.add(normalized);
            imageEntries.push({
                url: normalized,
                label: primitive.title ?? "",
                primitiveId: primitive.id,
                referenceId: primitive.referenceId
            });
            sourceIdsSet.add(primitive.id);
        }
    }
    return {
        images: imageEntries,
        total: imageEntries.length,
        offset: Math.min(Math.max(0, toInteger(config.image_startIndex ?? config.imageStartIndex ?? config.startIndex, 0)), imageEntries.length - 1),
        count: limit,
        sourceIds: Array.from(sourceIdsSet),
        skipSourceIds: Array.from(skipSourceIds)
    };
}

function preparePins(primitiveToPrepare, basePrimitive, stateId, myState){
    let pinIdx = 1

    function processPins(source){
        const sourcePins = Object.keys(source  ?? {})
        const fitleredPins = basePrimitive.renderConfig?.showAxisPin ? sourcePins : sourcePins.filter(d=>!source[d].axis)
        return fitleredPins.map((d,i)=>({
                name: d,
                label: source[d].name,
                rIdx: i
            }))
        .reduce((a,c)=>{
            c.idx = pinIdx
            a[c.name] = c
            pinIdx++
            return a
        }, {})
    }

    myState[stateId].inputPins = myState[stateId].inputPins ?? processPins(primitiveToPrepare.inputPins)
    if( !myState[stateId].outputPins ){
        myState[stateId].outputPins = processPins(primitiveToPrepare.outputPins )

        //if( basePrimitive.type === "flow" || basePrimitive.type === "page" ){
        if( basePrimitive.type === "flow" ){
            const tempOut = {}
            for(const pin of Object.values(myState[stateId].inputPins)){
                tempOut[pin.name] = {
                    ...pin,
                    internal: true,
                    showLabel: false,
                    rIdx: myState[stateId].inputPins[pin.name].rIdx,
                    idx: pinIdx++
                }
            }
            
            for(const pin of Object.values(myState[stateId].outputPins)){
                myState[stateId].inputPins[pin.name] = {
                    ...pin,
                    internal: true,
                    showLabel: false,
                    rIdx: myState[stateId].outputPins[pin.name].rIdx,
                    idx: pinIdx++
                }
            }
            myState[stateId].outputPins = {
            ...myState[stateId].outputPins,
            ...tempOut
            }
        }
    }   
    
}

function dropZoneToAxis(id){
    return id.split('-')
}

const TEXT_BASED_PLAIN_OBJECT_TYPES = new Set(["text", "structured_text"]);

function stableRepresentationString(value) {
    if (value === null || typeof value !== "object") {
        return JSON.stringify(value);
    }
    if (Array.isArray(value)) {
        return `[${value.map(item => stableRepresentationString(item)).join(",")}]`;
    }
    const entries = Object.keys(value)
        .sort()
        .map(key => `${JSON.stringify(key)}:${stableRepresentationString(value[key])}`);
    return `{${entries.join(",")}}`;
}

function cloneForTransport(value, seen = new WeakMap()) {
    if (value === null) {
        return null;
    }
    const type = typeof value;
    if (type === "number" || type === "string" || type === "boolean") {
        return value;
    }
    if (type === "bigint" || type === "function" || type === "symbol" || type === "undefined") {
        return undefined;
    }
    if (Array.isArray(value)) {
        if (seen.has(value)) {
            return seen.get(value);
        }
        const next = [];
        seen.set(value, next);
        value.forEach((item, index) => {
            const cloned = cloneForTransport(item, seen);
            if (cloned !== undefined) {
                next[index] = cloned;
            }
        });
        return next;
    }
    if (type === "object") {
        if (seen.has(value)) {
            return seen.get(value);
        }
        const next = {};
        seen.set(value, next);
        Object.keys(value).forEach(key => {
            const cloned = cloneForTransport(value[key], seen);
            if (cloned !== undefined) {
                next[key] = cloned;
            }
        });
        return next;
    }
    return undefined;
}

function pickPreferredPin(pinMap, preferredOrder = []) {
    if (!pinMap) {
        return undefined;
    }
    for (const key of preferredOrder) {
        const candidate = pinMap[key];
        if (candidate?.idx !== undefined) {
            return candidate.idx;
        }
    }
    const first = Object.values(pinMap).find((pin) => pin?.idx !== undefined);
    return first?.idx;
}

function parsePinsFromRelationship(rel) {
    if (!rel) {
        return undefined;
    }
    const token = rel.substring(rel.lastIndexOf(".") + 1);
    if (!token || !token.includes("_")) {
        return undefined;
    }
    const [leftPinName, rightPinName] = token.split("_");
    if (!leftPinName || !rightPinName) {
        return undefined;
    }
    return { leftPinName, rightPinName };
}

function buildSimpleRoutingLinks(boards, myState) {
    const links = [];
    const seen = new Set();

    const addLink = (left, right, leftPin, rightPin, mode) => {
        if (leftPin === undefined || rightPin === undefined) {
            return;
        }
        const id = `${left.id}.${leftPin}~${right.id}.${rightPin}`;
        if (seen.has(id)) {
            return;
        }
        seen.add(id);
        links.push({ left: left.id, right: right.id, leftPin, rightPin, mode });
    };

    for (const left of boards) {
        const leftState = myState[left.id];
        if (!leftState) {
            continue;
        }
        for (const right of boards) {
            if (!right || left.id === right.id) {
                continue;
            }
            const rightState = myState[right.id];
            if (!rightState) {
                continue;
            }

            const leftPathsFn = typeof left?.primitives?.paths === "function" ? left.primitives.paths : undefined;
            const rightPathsFn = typeof right?.primitives?.paths === "function" ? right.primitives.paths : undefined;

            const outputPaths = leftPathsFn ? leftPathsFn(right.id, "outputs") ?? [] : [];
            for (const rel of outputPaths) {
                const pins = parsePinsFromRelationship(rel);
                if (!pins) {
                    continue;
                }
                const leftPin = leftState.outputPins?.[pins.leftPinName]?.idx;
                const rightPin = rightState.inputPins?.[pins.rightPinName]?.idx;
                addLink(left, right, leftPin, rightPin, 3);
            }

            const inputPaths = rightPathsFn ? rightPathsFn(left.id, "inputs") ?? [] : [];
            for (const rel of inputPaths) {
                const pins = parsePinsFromRelationship(rel);
                if (!pins) {
                    continue;
                }
                const leftPin = leftState.outputPins?.[pins.leftPinName]?.idx;
                const rightPin = rightState.inputPins?.[pins.rightPinName]?.idx;
                addLink(left, right, leftPin, rightPin, 2);
            }

            const importsProxy = right?.primitives?.imports;
            if (importsProxy?.allIds?.includes(left.id)) {
                if( left.type === "flow" && right.originId === left.id){
                    continue
                }
                const leftPin = pickPreferredPin(leftState.outputPins, ["impout", "output"]);
                const rightPin = pickPreferredPin(rightState.inputPins, ["impin", "input"]);
                addLink(left, right, leftPin, rightPin, 1);
            }
        }
    }

    return links;
}

function buildLegacyRoutingLinks(boards, myState) {
    const itemCache = {};
    function getItemList(item) {
        let list = itemCache[item.id];
        if (!list) {
            itemCache[item.id] = item.itemsForProcessing;
            list = itemCache[item.id];
        }
        return list ?? [];
    }

    return boards
        .map((left) => {
            let segmentSummaries;
            return boards
                .map((right) => {
                    if (right.type === "element") {
                        return;
                    }
                    if (right.referenceId === 118) {
                        return;
                    }
                    if (left.id !== right.id) {
                        if (right.parentPrimitiveIds.includes(left.id)) {
                            const sources = right.parentPrimitiveIdsAsSource;
                            if (sources.length > 0) {
                                const ids = getItemList(left).map((d) => d.id);
                                if (sources.some((d) => ids.includes(d))) {
                                    return { left: left.id, right: right.id, mode: 0 };
                                }
                            }
                            if (left.type === "flow") {
                                if (right.primitives.imports.allIds.includes(left.id)) {
                                    const rightPin = myState[right.id].inputPins.impin?.idx;

                                    const outputPinCheck = Object.entries(left.primitives.outputs).filter((d) =>
                                        d[1].includes(right.id)
                                    );
                                    if (outputPinCheck) {
                                        const matchingPins = outputPinCheck.map((d) => d[0].split("_")[0]);
                                        return matchingPins
                                            .map((d) => {
                                                const leftPin = myState[left.id].outputPins[d]?.idx;
                                                if (leftPin) {
                                                    return {
                                                        left: left.id,
                                                        right: right.id,
                                                        leftPin,
                                                        rightPin,
                                                        mode: 1,
                                                    };
                                                }
                                            })
                                            .filter(Boolean);
                                    }

                                    const leftPin = myState[left.id].outputPins.impin?.idx;
                                    return { left: left.id, right: right.id, leftPin, rightPin, mode: 1 };
                                }
                                if (right.primitives.inputs.allIds.includes(left.id)) {
                                    return right.primitives.paths(left.id, "inputs").map((rel) => {
                                        const pinNames = rel.substring(rel.lastIndexOf(".") + 1);
                                        const [leftPinName, rightPinName] = pinNames.split("_");
                                        const leftPin = myState[left.id].outputPins[leftPinName]?.idx;
                                        const rightPin = myState[right.id].inputPins[rightPinName]?.idx;
                                        return { left: left.id, right: right.id, leftPin, rightPin, mode: 2 };
                                    });
                                }
                            } else {
                                if (right.primitives.imports.allIds.includes(left.id)) {
                                    if (left.primitives.outputs.allIds.includes(right.id)) {
                                        return left.primitives.paths(right.id, "outputs").map((rel) => {
                                            const pinNames = rel.substring(rel.lastIndexOf(".") + 1);
                                            const [leftPinName, rightPinName] = pinNames.split("_");
                                            const leftPin = myState[left.id].outputPins[leftPinName]?.idx;
                                            const rightPin = myState[right.id].inputPins[rightPinName]?.idx;
                                            return { left: left.id, right: right.id, leftPin, rightPin, mode: 3 };
                                        });
                                    }
                                }
                                if (left.primitives.paths(right.id).filter((d) => d.startsWith(".axis.")).length > 0) {
                                    return;
                                }
                                const leftPin = myState[left.id].outputPins.impout?.idx;
                                const rightPin = myState[right.id].inputPins.impin?.idx;
                                return { left: left.id, right: right.id, leftPin, rightPin, mode: 4 };
                            }
                        } else if (left.type === "flow" && right.primitives.imports.allIds.includes(left.id)) {
                            const leftPin = myState[left.id].outputPins.output?.idx;
                            const rightPin = myState[right.id].inputPins.impin?.idx;
                            return { left: left.id, right: right.id, leftPin, rightPin, mode: 5 };
                        } else if (right.type === "flow" && right.primitives.outputs.allIds.includes(left.id)) {
                            const rels = right.primitives.paths(left.id, "outputs");
                            const relPins = rels
                                .map((rel) => {
                                    const pinNames = rel.substring(rel.lastIndexOf(".") + 1);
                                    const [leftPinName, rightPinName] = pinNames.split("_");
                                    const leftPinDef = myState[left.id].outputPins[leftPinName];
                                    if (leftPinDef && !leftPinDef.internal) {
                                        const leftPin = leftPinDef?.idx;
                                        const rightPin = myState[right.id].inputPins[rightPinName]?.idx;
                                        if (leftPin !== undefined && rightPin !== undefined) {
                                            return { left: left.id, right: right.id, leftPin, rightPin, mode: 6 };
                                        }
                                    }
                                })
                                .filter((d) => d);
                            if (relPins.length > 0) {
                                return relPins;
                            }
                        } else if (right.primitives.inputs.allIds.includes(left.id)) {
                            return right.primitives.paths(left.id, "inputs").map((rel) => {
                                const pinNames = rel.substring(rel.lastIndexOf(".") + 1);
                                const [leftPinName, rightPinName] = pinNames.split("_");
                                const leftPin = myState[left.id].outputPins[leftPinName]?.idx;
                                const rightPin = myState[right.id].inputPins[rightPinName]?.idx;
                                if (leftPin !== undefined && rightPin !== undefined) {
                                    return { left: left.id, right: right.id, leftPin, rightPin, mode: 7 };
                                }
                            });
                        } else {
                            const route = right.findImportRoute(left.id);
                            if (route.length > 0) {
                                for (const axis of ["row", "column"]) {
                                    continue;
                                }
                                const leftPin = myState[left.id].outputPins.impout?.idx;
                                const rightPin = myState[right.id].inputPins.impin?.idx;

                                return { left: left.id, right: right.id, leftPin, rightPin, mode: 9 };
                            } else {
                                if (right.type === "query" || right.type === "summary") {
                                    if (!segmentSummaries) {
                                        segmentSummaries = left.primitives.origin.allUniqueSegment
                                            .map((d) => [...d.primitives.origin.allUniqueSummary, ...d.primitives.origin.allUniqueQuery])
                                            .flat();
                                    }

                                    if (segmentSummaries.map((d) => d.id).includes(right.id)) {
                                        const out = [];
                                        let added = false;
                                        if (myState[left.id].columns && myState[left.id].rows) {
                                            myState[left.id].columns.forEach((column, cIdx) => {
                                                myState[left.id].rows.forEach((row, rIdx) => {
                                                    if (!added) {
                                                        const filter = [
                                                            PrimitiveConfig.encodeExploreFilter(
                                                                myState[left.id].axis.column,
                                                                column
                                                            ),
                                                            PrimitiveConfig.encodeExploreFilter(
                                                                myState[left.id].axis.row,
                                                                row
                                                            ),
                                                        ].filter((d) => d);

                                                        if (right.origin.doesImport(left.id, filter)) {
                                                            out.push({
                                                                left: left.id,
                                                                cell: `${cIdx}-${rIdx}`,
                                                                right: right.id,
                                                                mode: 10,
                                                            });
                                                            added = true;
                                                        }
                                                    }
                                                });
                                            });
                                        }
                                        if (!added) {
                                            out.push({ left: left.id, right: right.id, mode: 11 });
                                        }
                                        return out;
                                    }
                                }
                            }
                        }
                    }
                })
                .flat(Infinity);
        })
        .flat()
        .filter((d) => d);
}

function coerceNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
}

function buildRepresentationPayload(stateEntry, stageOptions = {}, renderOptions = {}) {
    if (!stateEntry?.object || !TEXT_BASED_PLAIN_OBJECT_TYPES.has(stateEntry.object.type)) {
        return null;
    }

    const baseContent = stateEntry.object;
    const fallbackFrame = stateEntry.primitive?.frames?.[stateEntry.id] ?? {};

    const width = coerceNumber(stageOptions.width ?? renderOptions.width ?? baseContent.width ?? fallbackFrame.width);
    const height = coerceNumber(stageOptions.height ?? renderOptions.height ?? baseContent.height ?? fallbackFrame.height);

    if (!width || !height) {
        return null;
    }

    const payloadContent = cloneForTransport(baseContent) ?? {};
    const payloadRenderOptions = {
        width,
        height,
    };

    const padding = stageOptions.padding ?? renderOptions.padding ?? baseContent.padding ?? baseContent.text_padding;
    if (padding !== undefined) {
        payloadRenderOptions.padding = padding;
    }

    const fontSize = coerceNumber(stageOptions.fontSize ?? renderOptions.fontSize ?? baseContent.fontSize);
    if (fontSize !== undefined) {
        payloadRenderOptions.fontSize = fontSize;
    }

    const lineHeight = coerceNumber(stageOptions.lineHeight ?? renderOptions.lineHeight ?? baseContent.lineHeight);
    if (lineHeight !== undefined) {
        payloadRenderOptions.lineHeight = lineHeight;
    }

    const columns = coerceNumber(stageOptions.columns ?? renderOptions.columns ?? baseContent.columns);
    if (columns !== undefined) {
        payloadRenderOptions.columns = columns;
    }

    const fontFamily = stageOptions.fontFamily ?? renderOptions.fontFamily ?? baseContent.fontFamily;
    if (fontFamily) {
        payloadRenderOptions.fontFamily = fontFamily;
    }

    const fontStyle = stageOptions.fontStyle ?? renderOptions.fontStyle ?? baseContent.fontStyle;
    if (fontStyle) {
        payloadRenderOptions.fontStyle = fontStyle;
    }

    const theme = stageOptions.theme ?? renderOptions.theme ?? baseContent.theme ?? stateEntry.theme;
    if (theme) {
        const themeClone = cloneForTransport(theme);
        if (themeClone !== undefined) {
            payloadRenderOptions.theme = themeClone;
        }
    }
    const themeStyle =
        stageOptions.themeStyle ??
        renderOptions.themeStyle ??
        baseContent.themeStyle ??
        stateEntry.primitive?.renderConfig?.themeStyle;
    if (themeStyle) {
        payloadRenderOptions.themeStyle = themeStyle;
    }

    const themeOverrides = stageOptions.themeOverrides ?? renderOptions.themeOverrides;
    const payloadThemeOverrides = themeOverrides ? cloneForTransport(themeOverrides) : undefined;

    const keyPayload = {
        content: payloadContent,
        renderOptions: payloadRenderOptions,
    };
    if (payloadThemeOverrides !== undefined) {
        keyPayload.themeOverrides = payloadThemeOverrides;
    }

    const signature = stableRepresentationString(keyPayload);

    return {
        payloadContent,
        payloadRenderOptions,
        payloadThemeOverrides,
        signature,
    };
}

function maybeTriggerRepresentationPass(stateEntry, stageOptions, renderOptions, rootState, pageInstance) {
    if (!stateEntry?.object || !TEXT_BASED_PLAIN_OBJECT_TYPES.has(stateEntry.object.type)) {
        return {
            content: stateEntry?.object,
            awaitingRepresentation: false,
        };
    }
    if(true || !stateEntry.primitive?.referenceParameters?.dynamicContent ){
        return {
            content: stateEntry.object,
            awaitingRepresentation: false,
        };
    }

    const payload = buildRepresentationPayload(stateEntry, stageOptions, renderOptions);
    if (!payload) {
        const representationState = stateEntry.representation;
        return {
            content: representationState?.result?.content ?? stateEntry.object,
            awaitingRepresentation: Boolean(representationState?.inFlight),
        };
    }

    if (!stateEntry.representation || stateEntry.representationFor !== pageInstance?.id) {
        stateEntry.representation = stateEntry.primitive.instanceConfig?.[pageInstance?.id]?.representation;
        if( stateEntry.representation ){
            console.log(`RECOVERED STATE`)
        }
    }
    stateEntry.representationFor = pageInstance?.id
                
    if (!stateEntry.representation) {
        stateEntry.representation = {};
    }

    const representationState = stateEntry.representation;
    const hasNewSignature = representationState.signature !== payload.signature;

    if (hasNewSignature) {
        representationState.signature = payload.signature;
        representationState.result = undefined;
        representationState.error = undefined;
    }

    if (hasNewSignature && !representationState.inFlight) {
        const targetFrameId = stateEntry?.id ?? stateEntry?.primitive?.id ?? null;
        const body = {
            content: payload.payloadContent,
            renderOptions: payload.payloadRenderOptions,
        };
        if (payload.payloadThemeOverrides !== undefined) {
            body.themeOverrides = payload.payloadThemeOverrides;
        }
        if (representationState.lastSourceHash) {
            body.previousHash = representationState.lastSourceHash;
        }

        representationState.inFlight = fetch("/api/representationPass", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
        })
            .then(response => {
                if (!response.ok) {
                    const error = new Error(`representationPass failed: ${response.status}`);
                    error.status = response.status;
                    throw error;
                }
                return response.json();
            })
            .then(result => {
                representationState.result = result;
                representationState.lastSourceHash = result?.sourceHash ?? null;
            })
            .catch(error => {
                representationState.error = error;
                console.error("representationPass error", error);
            })
            .finally(() => {
                representationState.inFlight = null;
                const canvasApi =
                    rootState?.current?.canvas ??
                    rootState?.canvas ??
                    rootState?.current?.canvas?.current ??
                    rootState?.canvas?.current ??
                    null;
                let didRefresh = false;
                if (targetFrameId && canvasApi?.refreshFrame) {
                    canvasApi.refreshFrame(targetFrameId);
                    didRefresh = true;
                } else if (targetFrameId && canvasApi?.current?.refreshFrame) {
                    canvasApi.current.refreshFrame(targetFrameId);
                    didRefresh = true;
                }
                const toStore = {...representationState}//, steps: representationState.steps.length}
                stateEntry.primitive.setField(`instanceConfig.${pageInstance.id}.representation`, toStore)
            });
    }

    return {
        content: representationState.result?.content ?? stateEntry.object,
        awaitingRepresentation: Boolean(representationState.inFlight),
    };
}

async function moveItemOnAxis(  primitive, axis, from, to ){
    if( axis){
        if( axis.type === "category"){
            console.log(`move ${primitive.title} from ${from} to ${to}`)
            if( from ){
                const prim = mainstore.primitive(from)
                if( prim ){
                    await prim.removeRelationship( primitive, 'ref')
                }
            }
            if( to ){
                const prim = mainstore.primitive(to)
                if( prim ){
                    await prim.addRelationship( primitive, 'ref')
                }
            }
        }
    }
}
async function moveItemWithinFrame(primitiveId, startZone, endZone, frame){
    console.log(`${primitiveId} - >${startZone} > ${endZone}`)
    const primitive = mainstore.primitive(primitiveId)
    if( primitive ){
        const [sc,sr] = dropZoneToAxis( startZone)
        const [ec,er] = dropZoneToAxis( endZone )
        if( sc !== ec){
            await moveItemOnAxis(primitive, frame.axis.column, frame.extents.column[sc]?.idx, frame.extents.column[ec]?.idx)
        }
        if( sr !== er){
            await moveItemOnAxis(primitive, frame.axis.row, frame.extents.row[sr]?.idx, frame.extents.row[er]?.idx)
        }
    }

}
    function getLocalPrimitiveListForBoardId( id, myState ){
        let localItems
        if( myState[id].axisSource){

            if( ! myState[myState[id].axisSource.id] && !myState[id].axisSource.inFlow ){
                SharedPrepareBoard(myState[id].axisSource, myState)
                myState[myState[id].axisSource.id].skipNextBuild = true
            }
            const source = myState[myState[id].axisSource.id]
            if( source ){
                if( source.data){
                    localItems = source.data.cells.flatMap(d=>d.items)
                }else if( source.primitiveList){
                    localItems = source.primitiveList
                }else{
                    localItems = source.list?.map(d=>d.primitive)
                }
            }else{
                localItems = myState[id].primitiveList
            }                
        }else{
                localItems = myState[id].primitiveList

        }
        return localItems
    }


function preparePageElements( d, pageState ){
    
    if( d.type !== "page"){
        return []
    }
    let configPage = d.configParent ?? d
    let page = d 

    pageState.subpages ||= {}
    pageState.subpages[d.id] ||= {}
    
    let tempState = pageState.subpages[d.id]
    tempState.renderContexts ||= new Map()
    tempState.pageInputs = d.inputs
    let childNodes = configPage.primitives.origin.allUniqueElement
    childNodes = [...childNodes.filter(d=>d.referenceId === PrimitiveConfig.Constants.SHAPE_ELEMENT && d.referenceParameters.style === "rect"), ...childNodes.filter(d=>d.referenceId === PrimitiveConfig.Constants.SHAPE_ELEMENT && d.referenceParameters.style !== "rect"), ...childNodes.filter(d=>d.referenceId !== PrimitiveConfig.Constants.SHAPE_ELEMENT )]
    
    if( tempState[configPage.id]){
        console.log(`REUSING SUB PAGE STATE ${d.id}`)
    }else{
        tempState[configPage.id] = {
            id: configPage.id, 
            primitive: configPage,
            underlying: page
        }


        const pageInputs = d.inputs
        const pageOutputs = configPage.primitives.outputs
        for(let child of childNodes){
            const {x,y,s, ...renderConfigOverride} = configPage.frames?.[child.id] ?? {x: 0, y: 0, s: 1}
            tempState[child.id] = {
                id: child.id, 
                primitive: child, 
                renderConfigOverride,
                page: configPage,
                position: {x,y,s},
                renderConfigOverride
            }
        }
    }
    const pageEntry = tempState[configPage.id]
    if( pageEntry ){
        const emptyElementIds = childNodes
            .filter(child => tempState[child.id]?.empty || tempState[child.id]?.noContent)
            .map(child => child.id)
        pageEntry.emptyContentElements = emptyElementIds
        pageEntry.emptyContent = emptyElementIds.length > 0
    }
    
    return [childNodes.map(d=>{
        SharedPrepareBoard( d, tempState)
        const state = {
            renderContexts: tempState.renderContexts,
            subpages: tempState.subpages,
            ...tempState,
            [configPage.id]:{
                id: configPage.id, 
                primitive: configPage,
                underlying: page
            },
            [d.id]: tempState[d.id],
        }
        const subs = [state[d.id].axisSource, ...(state[d.id].pinSource ?? [])].filter(d=>d)
        let buildNew = false
        for( let axisSource of subs){
            if(!state[axisSource.id]){
                let axisBoard = {}
                
                if (axisSource.inFlow && axisSource.configParent.flowElement) {
                    const parent = axisSource.configParent
                    const flowInstance = axisSource.origin
                    const flow = parent.origin
                    if( flowInstance.origin.id !== flow.id){
                        throw `Flow mismatch ${flowInstance.id} ${flow.id}`
                    }
                    axisBoard[parent.id] = {
                        id: parent.id,
                        underlying: axisSource,
                        inFlow: true,
                        flow,
                        flowInstance
                    }
                    axisSource = parent
                }
                
                SharedPrepareBoard( axisSource, axisBoard)
                state[axisSource.id] = axisBoard[axisSource.id]
                buildNew = true
            }
        }
        if( buildNew ){
            SharedPrepareBoard( d, state)                    
        }
        
        return {
            ...tempState[d.id].position,
            ...tempState[d.id].renderConfigOverride,
            primitive: d,
            state
        }
    })]
}

function renderSubBoard(d, stageOptions = {}){
    if( d.primitive){
        const output = SharedRenderView( d.primitive, {}, d.state, stageOptions)
        if( output?.items ){
            const rendered = output.items(stageOptions)
            if( rendered){
                rendered.scale({x: d.s, y: d.s})
                rendered.attrs.id = d.primitive.id
                return {
                    x: d.x,
                    y: d.y,
                    state: d.state,
                    rendered
                }
            }
        }
    }
    return undefined
}

let mainstore = MainStore()

function collectCategoriesInScopeForView(basePrimitive, viewState) {
    if (!basePrimitive || !viewState) {
        return [];
    }

    const categories = [];
    const seen = new Set();

    const addCategory = (cat) => {
        if (!cat || cat.type !== "category") {
            return;
        }
        if (seen.has(cat.id)) {
            return;
        }
        seen.add(cat.id);
        categories.push(cat);
    };

    const addCategoryList = (list) => {
        if (!list) {
            return;
        }
        for (const item of list) {
            addCategory(item);
        }
    };

    addCategoryList(basePrimitive?.primitives?.allUniqueCategory);
    addCategoryList(basePrimitive?.primitives?.origin?.allUniqueCategory);

    const ancestorContent = basePrimitive?.findParentPrimitives?.({ type: ["view", "query", "categorizer"] }) ?? [];
    addCategoryList(ancestorContent.flatMap(parent => parent?.primitives?.origin?.allUniqueCategory ?? []));

    const ancestorBoards = basePrimitive?.findParentPrimitives?.({ type: ["board", "flow"] }) ?? [];
    addCategoryList(ancestorBoards.flatMap(parent => parent?.primitives?.origin?.allUniqueCategory ?? []));

    const origin = basePrimitive?.origin;
    if (origin?.type === "flow" || origin?.type === "flowinstance") {
        const assignable = origin?.primitives?.origin?.allCategorizer?.filter(cat => cat?.metadata?.mode === "assign") ?? [];
        addCategoryList(assignable);
    }

    if (viewState?.axis) {
        for (const axisKey of ["row", "column"]) {
            const axisConfig = viewState.axis[axisKey];
            if (axisConfig?.type === "category" && axisConfig.primitiveId) {
                const axisPrimitive = mainstore.primitive(axisConfig.primitiveId);
                addCategory(axisPrimitive);
            }
        }
    }

    if (Array.isArray(viewState?.pinSource)) {
        viewState.pinSource.forEach(sourcePrimitive => {
            addCategoryList(sourcePrimitive?.primitives?.origin?.allUniqueCategory);
        });
    }

    const scopeItems = uniquePrimitives([
        ...(Array.isArray(viewState?.primitiveList) ? viewState.primitiveList : []),
        ...(Array.isArray(viewState?.list) ? viewState.list.map(entry => entry?.primitive).filter(Boolean) : []),
    ]);

    scopeItems.forEach(item => {
        addCategoryList(item?.primitives?.allUniqueCategory);
        addCategoryList(item?.primitives?.origin?.allUniqueCategory);
        if (Array.isArray(item?.parentPrimitives)) {
            item.parentPrimitives.forEach(parent => {
                if (parent?.type === "category") {
                    addCategory(parent);
                    if (Array.isArray(parent.parentPrimitives)) {
                        parent.parentPrimitives.forEach(grandParent => addCategory(grandParent));
                    }
                }
            });
        }
    });

    return categories;
}

function attachCategoryProgressIndicator(baseRenderView, basePrimitive, viewState) {
    if (!baseRenderView || !basePrimitive || !viewState) {
        return;
    }

    const categories = collectCategoriesInScopeForView(basePrimitive, viewState);
    const categoryIds = categories.map(cat => cat.id);

    const watchIds = new Set(viewState.internalWatchIds ?? []);
    categoryIds.forEach(id => watchIds.add(id));
    viewState.internalWatchIds = Array.from(watchIds);
    viewState.categoryScopeIds = categoryIds;

    if (categoryIds.length === 0) {
        viewState.categoryIndicatorBuilder = undefined;
        return;
    }

    const buildCategoryIndicators = () => {
        const trackedCategories = categoryIds.map(id => mainstore.primitive(id)).filter(Boolean);
        const running = trackedCategories.filter(cat => cat?.isRunning);

        if (running.length === 0) {
            return undefined;
        }

        const progressValues = running
            .map(cat => {
                let value = cat.percentageProgress;
                if (typeof value === "number" && !Number.isNaN(value)) {
                    if (value <= 1) {
                        return Math.max(0, Math.min(1, value));
                    }
                    return Math.max(0, Math.min(1, value / 100));
                }

                const progressText = cat.progress;
                if (typeof progressText === "string") {
                    const match = progressText.match(/([0-9]+(?:\.[0-9]+)?)%/);
                    if (match) {
                        const parsed = parseFloat(match[1]);
                        if (!Number.isNaN(parsed)) {
                            return Math.max(0, Math.min(1, parsed / 100));
                        }
                    }
                }
                return undefined;
            })
            .filter(value => value !== undefined);

        const averageProgress = progressValues.length > 0
            ? Math.max(0, Math.min(1, progressValues.reduce((a, c) => a + c, 0) / progressValues.length))
            : undefined;

        return {
            count: running.length,
            progress: averageProgress
        };
    };

    viewState.categoryIndicatorBuilder = buildCategoryIndicators;
    baseRenderView.indicators = () => buildCategoryIndicators();
}

const FLOW_ACTIVE_STATUSES = new Set(["running", "pending", "queued"]);

function primitiveHasActiveProcessing(primitive) {
    const processing = primitive?.processing;
    if (!processing || typeof processing !== "object") {
        return false;
    }
    for (const value of Object.values(processing)) {
        if (!value || typeof value !== "object") continue;
        const status = String(value.status ?? "").toLowerCase();
        if (FLOW_ACTIVE_STATUSES.has(status)) {
            return true;
        }
    }
    return false;
}

function attachFlowActiveChip(baseRenderView, basePrimitive, viewState) {
    if (!baseRenderView || !basePrimitive || !viewState) {
        return;
    }
    if (basePrimitive.type !== "flow" && basePrimitive.type !== "flowinstance") {
        return;
    }

    const flowInstances = basePrimitive.type === "flow"
        ? (basePrimitive.primitives?.origin?.allFlowinstance ?? [])
        : [];
    const watchIds = new Set(viewState.internalWatchIds ?? []);
    watchIds.add(basePrimitive.id);
    flowInstances.forEach(fi => fi?.id && watchIds.add(fi.id));
    viewState.internalWatchIds = Array.from(watchIds);
    viewState.flowActiveScopeIds = [basePrimitive.id, ...flowInstances.map(fi => fi?.id).filter(Boolean)];

    const buildFlowActive = () => {
        if (primitiveHasActiveProcessing(basePrimitive)) {
            return true;
        }
        if (basePrimitive.type !== "flow") {
            return false;
        }
        // re-read flow instances in case set changes between renders
        const current = basePrimitive.primitives?.origin?.allFlowinstance ?? [];
        return current.some(fi => primitiveHasActiveProcessing(fi));
    };

    viewState.flowActiveBuilder = buildFlowActive;
    baseRenderView.activeChip = buildFlowActive;
}

function getPageEmptyInfo(pagePrimitive, myState) {
    if (!pagePrimitive || !myState) {
        return { hasEmptyContent: false, emptyElementIds: [] };
    }
    const pageState = myState[pagePrimitive.id];
    const configPage = pagePrimitive.configParent ?? pagePrimitive;
    const pageSubpages = pageState?.subpages?.[pagePrimitive.id];
    const pageEntry = pageSubpages?.[configPage.id];
    const emptyElementIds = Array.isArray(pageEntry?.emptyContentElements) ? pageEntry.emptyContentElements : [];
    return {
        hasEmptyContent: Boolean(pageEntry?.emptyContent),
        emptyElementIds
    };
}
function SharedRenderView(d, primitive, myState, stageOptions = {}) {
    const view = myState[d.id];
    const primitiveToRender = view.primitive.type === "element" ? view.primitive: (view.underlying ?? view.primitive);
    // Base view and options
    const renderOptions = {
        ...(view.primitive?.renderConfig ?? {}),
        ...view.renderConfigOverride,
        theme: view.theme
    };
    if (view.renderContextId) {
      renderOptions.renderContextId = view.renderContextId;
    }
    if (view.widgetConfig) {
      renderOptions.widgetConfig = view.widgetConfig;
    }
    if(stageOptions.theme){
      renderOptions.theme = stageOptions.theme;
    }
  
    const shouldHydrateHeight = (
      view.primitive.type === "element" ||
      view.config === "datatable" ||
      view.config === "full" ||
      view.config === "plain_object" ||
      view?.viewConfig?.resizable === true
    )
    // Merge size options if available
    const configNames = shouldHydrateHeight ? ["width", "height"] : ["width"]
    const sizeSource = view.parentRender ? myState[view.parentRender].primitive.frames : primitive.frames;
    if (sizeSource?.[d.id]) {
      configNames.forEach(name => {
        if (sizeSource[d.id][name] !== undefined) {
          renderOptions[name] = sizeSource[d.id][name];
        }
      });
    }
    if( isHeightDebugEnabled() && (primitiveToRender?.referenceId === (PrimitiveConfig.Constants.TIMELINE_EVENT_SUMMARY ?? 178) || renderOptions?.debugHeight === true) ){
      console.debug("[height-debug][BoardViewer][hydrate]", {
        frameId: d.id,
        viewConfig: view.config,
        canHydrateHeight: shouldHydrateHeight,
        hydratedWidth: renderOptions.width,
        hydratedHeight: renderOptions.height,
        sizeSource: sizeSource?.[d.id],
      })
    }
  
    // Determine what primitive to render
  
    // Common properties
    const pins =
      view.primitive.type === "element" ? undefined: {
            input: Object.values(view.inputPins),
            output: Object.values(view.outputPins)
          };
    const frameless = view.inPage;
    const titleAlwaysPresent = !view.noTitle && !(view.widgetConfig !== undefined || view.config === "widget");
    const title = view.noTitle ? undefined : () =>
          view.title ??
          `${d.title} - #${d.plainId}${
            view.underlying ? ` (${primitiveToRender.plainId})` : ""
          }`;

    //const canvasMargin = view.inPage ? [0, 0, 0, 0] :  ["external","search"].includes(d.type) ? [0,0,0,0] : [10, 10, 10, 10];
    const canvasMargin = view.inPage ? [0, 0, 0, 0] :  ["external","search"].includes(d.type) ? [0,0,0,0] : [10, 10, 10, 10];
    const frameColor = PrimitiveConfig.typeConfig[d.type]?.render?.background
  
    // Optional indicator builder
    const indicators = undefined//view.primitive.flowElement ? () => buildIndicators(primitiveToRender, undefined, undefined, myState) : undefined;
  
    const prepareSubBoards = d => preparePageElements(d, view);
  
    const mapMatrix = (stageOptions, d, view) => {
      let dataSource = view;
      if (view.axisSource) {
        if (view.axisSource.inFlow && view.axisSource.configParent?.flowElement) {
          dataSource = myState[view.axisSource.configParent.id];
        } else {
          dataSource = myState[view.axisSource.id];
        }
        if (!dataSource) {
          console.warn(`Couldn't find config for axis Source ${view.axisSource.id}`);
          dataSource = view;
        }
      }
      return renderMatrix(primitiveToRender, dataSource.list ?? [], {
        axis: dataSource.axis,
        columnExtents: dataSource.columns,
        rowExtents: dataSource.rows,
        viewConfig: view.viewConfig,
        ...stageOptions,
        ...renderOptions,
        renderOptions,
        toggles: view.toggles,
        data: view.renderData,
        expand: Object.keys(primitive.frames?.[d.id]?.expand ?? {})
      });
    };
  
    // Build base render view object
    const baseRenderView = {
      id: d.id,
      parentRender: view.parentRender,
        backgroundLayer : view.backgroundLayer,
      isContainer: d.type === "page",
      pins,
      frameless,
      ringless: d.type === "page",
      frameColor,
      title,
      titleAlwaysPresent,
      indicators,
      canvasMargin
    };
    
    if( myState[d.id].empty || myState[d.id].noContent){
        const page = myState[d.id].primitive.origin
        let sizing = {}
        if( page?.type === "page"){
            sizing = page.frames?.[d.id]?.placeholder?.sizing
        }
        const configuredErrorMessage = myState[d.id].errorMessage
        const statusMessage = myState[d.id].errors
            ? { error: true, message: configuredErrorMessage ?? "Error creating this content" }
            : (myState[d.id].noContent ? { message: "Nothing to show" } : { message: "Waiting" });
        const placeholderView = {
          ...baseRenderView,
          canChangeSize: true,
          items: stageOptions => {
            return renderPlaceholder({
                ...stageOptions, 
                ...renderOptions, renderOptions,
                ...(sizing ?? {}),
                status: statusMessage,
                scale: page.frames[d.id]?.s ? (1 / page.frames[d.id]?.s) : 1,
                style: "text"
            });
          }
        }
        attachCategoryProgressIndicator(placeholderView, view.primitive ?? d, view);
        return placeholderView
    }
  
    // Determine "items" and "canChangeSize" based on view.config
    let renderView = null;
    switch (view.config) {
      case "plain_object":
        renderView = {
          ...baseRenderView,
          frameless: true,
          canChangeSize: "plain",
          items: stageOptions => {
            const stateEntry = myState[d.id];
            const pageEntry = myState[ stateEntry.page.id ]
            const pageInstance = pageEntry?.underlying ?? pageEntry?.primitive
            const { content: processed, awaitingRepresentation } = maybeTriggerRepresentationPass(stateEntry, stageOptions, renderOptions, myState, pageInstance);
            if (awaitingRepresentation) {
              return renderPlaceholder({
                ...stageOptions,
                ...renderOptions,
                renderOptions,
                style: "text",
                animate: true,
              });
            }
            const data = processed ?? stateEntry?.object ?? {};
            return renderPlainObject({ ...data, ...stageOptions, ...renderOptions, renderOptions });
          }
        };
        break;
      case "datatable":
        renderView = {
          ...baseRenderView,
          canChangeSize: view?.viewConfig?.resizable ? "plain" : true,
          items: stageOptions => {
            const data = myState[d.id].data;
            const viewConfig = myState[d.id].viewConfig;

            const configNames = ["width", "height"]
            const sizeSource = view.parentRender ? myState[view.parentRender].primitive.frames : primitive.frames;
            if (sizeSource?.[d.id]) {
            configNames.forEach(name => {
                if (sizeSource[d.id][name] !== undefined) {
                renderOptions[name] = sizeSource[d.id][name];
                }
            });
            }

            return renderDatatable({
                id: d.id,
                primitive: d,
                data,
                stageOptions,
                expand: Object.keys(primitive.frames?.[d.id]?.expand ?? {}),
                renderOptions,
                viewConfig,
                utils: { prepareBoards: prepareSubBoards, renderBoard: renderSubBoard },
                theme: renderOptions.theme
            });
          }
        };
        break;
      case "observation":
        renderView = {
          ...baseRenderView,
          canChangeSize: true,
          items: stageOptions =>
            RenderPrimitiveAsKonva(primitiveToRender, {
              ...stageOptions,
              ...renderOptions,
              config: "observation",
              observationData: view.renderData ?? {
                tableData: view.data,
                viewConfig: view.viewConfig,
                takeaway: primitiveToRender.referenceParameters?.takeaway,
                description: primitiveToRender.referenceParameters?.description,
                summaryInclude: primitiveToRender.referenceParameters?.summaryInclude,
                summaryComparison: primitiveToRender.referenceParameters?.summaryComparison
              }
            })
        };
        break;
  
      case "full": {
        const primitiveRenderConfig = view?.viewConfig?.renderType && view.viewConfig.renderType !== "full"
          ? view.viewConfig.renderType
          : undefined;
        let renderFunc = stageOptions =>
          RenderPrimitiveAsKonva(primitiveToRender, {
            ...stageOptions,
            ...renderOptions,
            ...(primitiveRenderConfig ? {config: primitiveRenderConfig} : {}),
            renderOptions
          });
        // Special case for a reference
        if (d.referenceId === 118) {
          let boardToCombine = d.primitives.imports.allItems;
          if (d.order) {
            boardToCombine.sort((a, b) => d.order.indexOf(a.id) - d.order.indexOf(b.id));
          }
          if (boardToCombine.length > 0) {
            renderFunc = stageOptions => {
              const partials = boardToCombine.map(item => {
                const board = myState[item.id];
                return {
                  primitive: item,
                  axis: board.axis,
                  columnExtents: board.columns,
                  rowExtents: board.rows,
                  viewConfig: board.viewConfig,
                  list: board.list
                };
              });
              return RenderPrimitiveAsKonva(primitiveToRender, {
                ...stageOptions,
                ...renderOptions,
                ...(primitiveRenderConfig ? {config: primitiveRenderConfig} : {}),
                renderOptions,
                partials
              });
            };
          }
        }
        const fullResizeMode = view?.viewConfig?.resizeMode ?? (view?.viewConfig?.resizable ? "plain" : "width")
        renderView = {
          ...baseRenderView,
          canChangeSize: fullResizeMode,
          items: renderFunc
        };
        break;
      }
  
      case "cat_overview":
      case "word_cloud":
        renderView = {
          ...baseRenderView,
          canChangeSize: "width",
          items: stageOptions =>
            RenderPrimitiveAsKonva(primitiveToRender, {
              ...stageOptions,
              ...renderOptions,
              renderOptions,
              config: view.config,
              data: view.renderData
            })
        };
        break;
      case "chart":
      case "dial":
        let dataSource = view;
        if (view.axisSource) {
            if (view.axisSource.inFlow && view.axisSource.configParent?.flowElement) {
            dataSource = myState[view.axisSource.configParent.id];
            } else {
            dataSource = myState[view.axisSource.id];
            }
            if (!dataSource) {
            console.warn(`Couldn't find config for axis Source ${view.axisSource.id}`);
            dataSource = view;
            }
        }
        if (dataSource?.data) {
            const renderContextId = `${d.id}`;
            myState.renderContexts ||= new Map();
            myState.renderContexts.set(renderContextId, {
                dataTable: dataSource.data,
                primitive: dataSource.underlying ?? dataSource.primitive ?? view.primitive ?? primitive,
                viewConfig: dataSource.viewConfig ?? view.viewConfig,
            });
            myState[d.id].renderContextId = renderContextId;
        }
        const catData = {}
        let useJoint = view.config === "dial"
        let mapped = {}
        const pass = ["clearly", "likely", ""]
        for(const c of dataSource.columns){
            for(const r of dataSource.rows){
                let idx = [r.idx, c.idx].filter(d=>d).join("-") ?? "base"
                let label
                if( useJoint ){
                    label = [r.label, c.label].every(d=>pass.includes(d)) ? pass[0] : "not at all"
                }else{
                    label = [r.label, c.label].filter(d=>d).join(" - ") ?? "base"
                }
                mapped[idx] = label
                const axisPrims = [r.primitive, c.primitive].filter(d=>d)
                if( !catData[label] ){
                    catData[label] = {label, items: [], count: 0, axisPrims}
                }
            }
        }
        for(const p of dataSource.list ){
            for(const c of [p.column].flat() ?? [undefined]){
                for(const r of [p.row].flat() ?? [undefined]){
                    const idx = mapped[[r, c].filter(d=>d).join("-") ?? "base"]
                    if( catData[idx]){
                        catData[idx].items.push( p.primitive)
                        catData[idx].count++
                    }
                }
            }
        }
        renderView = {
          ...baseRenderView,
          canChangeSize: "width",
          items: stageOptions =>
            RenderPrimitiveAsKonva(primitiveToRender, {
              ...stageOptions,
              ...renderOptions,
              renderOptions,
              config: view.config,
              data: Object.values(catData)
            })
        };
        break;
      case "page": {
        const renderFunc = stageOptions =>
          RenderPrimitiveAsKonva(primitiveToRender, { ...stageOptions, ...renderOptions, renderOptions, data: view.renderData });
        renderView = {
          ...baseRenderView,
          utils: view.renderSubPages || RENDERSUB
            ? {
                prepareBoards: prepareSubBoards,
                renderBoard: (d, opts = {}) => renderSubBoard(d, { ...opts, theme: renderOptions.theme }),
              }
            : undefined,
          canChangeSize: true,
          canvasMargin: [0, 0, 0, 0],
          items: stageOptions => renderFunc(stageOptions),
        };
        break;
      }
  
      case "flow": {
        const renderFunc = stageOptions =>
          RenderPrimitiveAsKonva(primitiveToRender, { ...stageOptions, ...renderOptions, renderOptions, data: view.renderData });
        attachFlowActiveChip(baseRenderView, primitiveToRender, view);
        renderView = {
          ...baseRenderView,
          resizeForChildren: true,
          ringless: true,
          routeInternal: true,
          canChangeSize: true,
          canvasMargin: [0, 0, 0, 0],
          items: renderFunc,
          bgFill: "transparent",
          flow: true
        };
        break;
      }
  
      case "widget": {
        const data = view.renderData;
        if (view.inFlow) {
          data.basePrimitive = view.primitive;
        }
        const renderFunc = stageOptions =>
          RenderPrimitiveAsKonva(primitiveToRender, {
            ...stageOptions,
            ...renderOptions,
            config: "widget",
            data
          });
        renderView = {
          ...baseRenderView,
          canChangeSize: "width",
          canvasMargin: [2, 2, 2, 2],
          items: renderFunc
        };
        break;
      }
  
      case "report_set":
        renderView = {
          ...baseRenderView,
          canChangeSize: "width",
          items: stageOptions =>
            RenderSetAsKonva(primitiveToRender, view.list, {
              referenceId: primitiveToRender.referenceId,
              ...stageOptions,
              ...renderOptions,
              axis: view.axis,
              data: view.renderData,
              extents: { column: view.columns, row: view.rows }
            })
        };
        break;
  
      default:
        if (view.viewConfig?.matrixType) {
            const rows = ()=>view.rows.map(item => ({
                ...item,
                primitive: mainstore.primitive(item.idx)
            })).sort((a,b)=>b.count - a.count)

            let columns = ()=>view.columns.map(item => ({
                ...item,
                primitive: mainstore.primitive(item.idx)
            }))

            renderView = {
                ...baseRenderView,
                canChangeSize: view?.viewConfig?.resizable,
                items: stageOptions =>
                    RenderSetAsKonva(primitiveToRender, view.list, {
                        ...stageOptions,
                        ...renderOptions,
                        renderOptions,
                        axis: view.axis,
                        allocations: view.filteredAllocations,
                        extents: {
                        column: columns(),
                        row: rows()
                        },
                        config: view.viewConfig?.matrixType
                    })
            };
        } else {
          renderView = {
            ...baseRenderView,
            utils: { prepareBoards: prepareSubBoards, renderBoard: renderSubBoard },
            canChangeSize: true,//view?.viewConfig?.resizable,
            items: stageOptions => mapMatrix(stageOptions, d, view)
          };
        }
        break;
    }
  
    attachCategoryProgressIndicator(renderView, view.primitive ?? d, view);

    return renderView;
  }
    function SharedPrepareBoard(d, myState, element, forceViewConfig){
        let stateId = element ? element.id : d.id
        if( myState[stateId]?.skipNextBuild ){
            console.log(`++++ SKIP THIS BUILD of ${stateId}`)
            myState[stateId].skipNextBuild = false
            return
        }
        let didChange = false
        let boardsToRefresh = []
        if( !myState[stateId]){
                myState[stateId] = {id: stateId}
        }
        const basePrimitive = d
        if( d.inFlow ){
            if( !myState[d.originId]){
                if(d.type === "flow"){
                    myState[stateId].flowInstance = myState.mainFlowInstance
                }else{
                    if( !myState[stateId].underlying ){
                        const instanceForFlowInstance = d.primitives.config.allItems.find(d=>d.parentPrimitiveIds.includes( myState.mainFlowInstance?.id))
                        myState[stateId].underlying = instanceForFlowInstance
                    }
                }
            }
        }

        let primitiveToPrepare = myState[stateId].underlying ?? d
        let renderType = primitiveToPrepare.type

        if( d.type === "element" ){
            const pagePrimitive = myState[stateId].page
            const pageState = pagePrimitive ? myState[pagePrimitive.id] : undefined
            const config = basePrimitive.getConfig

            const format = {
                fontSize: config?.fontSize,
                fontStyle: config?.fontStyle,
                fontFamily: config?.fontFamily,
                align: config?.align,
                heading: config?.heading,
                compose: config?.compose,
                fill: config.fill,
                stroke: config.stroke,
                style: config.style,
                columns: config?.columns,
                columnSpacing: config?.column_spacing ?? config?.columnSpacing,
                text_padding: config.text_padding,
                text_color: config.text_color,
            }
            if( d.referenceId ===  PrimitiveConfig.Constants.SHAPE_ELEMENT){//{pageState?.underlying){
                let text = config.text
                
                myState[stateId].object = {
                    type: "shape",
                    text: text,
                    ...format
                } 
                myState[stateId].config = "plain_object"
                myState[stateId].primitive = basePrimitive
                renderType = "plain_object"
                didChange = true
            }else{
                const pageInstance = pageState.underlying ?? pageState.primitive
                const pageOutputs = pageState.primitive.primitives.outputs
                const pins = Object.keys(pageOutputs ?? {}).filter(d2=>pageOutputs[d2].allIds.includes(d.id)).map(d=>d.split("_")[0])
                if( pins.length > 0){
                    let pageInputs = pageInstance.inputs
                    let inputs = pins.flatMap(pin=>pageInputs[pin]?.data ?? [])
                    let sources = pins.flatMap(pin=>pageInputs[pin]?.source ?? [])
                   

                    if( pageInputs[pins[0]]?.config === "primitive"){
                        // Do Ancestors
                        if( config.source_items){
                            let changed = false
                            do{
                                changed = false
                                function getSources(d){
                                    if( d.referenceId === 82 || d.type == "summary" || d.type === "query"){
                                        changed = true
                                        return [d.primitives.source.allItems,d.primitives.link.allItems].flat().map(d=>getSources(d))
                                    }else{
                                        return d
                                    }
                                }
                                inputs = uniquePrimitives( inputs.map(d=>getSources(d)).flat(Infinity))
                            }while(changed)

                        }
                        myState[stateId].originalList = inputs
                        if( config.flowinstance){
                            inputs = [pageInstance.origin].filter(Boolean)
                            
                            /*inputs = uniquePrimitives(inputs.flatMap(d=>d.findParentPrimitives({type: "flowinstance", first:true})))
                            if( inputs.length === 0){
                                inputs = [pageInstance.origin]
                            }*/
                        }else{
                            if( config.ancestor){
                                const rel = config.ancestor
                                if( rel && rel[0] === "SEGMENT"){
                                    inputs = uniquePrimitives( inputs.flatMap(d=>d.findParentPrimitives({type: ["segment"]})) ).slice(-1)
                                }else{
                                    inputs = uniquePrimitives( inputs.flatMap(d=>d.relationshipAtLevel(rel,rel.length)) ).slice(-1)
                                }
                                if( inputs[0]?.type === "flowinstance"){
                                    inputs = uniquePrimitives(inputs.flatMap(d=>d.itemsForProcessing))
                                }
                            }
                        }
                    }

                   
                    inputs = inputs.filter(d=>d)
                    if( pageInputs[pins[0]]?.config === "primitive"){
                        if( d.referenceId === PrimitiveConfig.Constants.IMAGE_ELEMENT ){
                            const resolvedFlowInstanceId = myState[stateId]?.flowInstance?.id
                                ?? pageState?.flowInstance?.id
                                ?? pageState?.underlying?.findParentPrimitives?.({type: "flowinstance", first: true})?.[0]?.id
                                ?? myState.mainFlowInstance?.id
                            const galleryOverrides = (resolvedFlowInstanceId && basePrimitive.instanceConfig?.[resolvedFlowInstanceId]?.imageGallery)
                                ? basePrimitive.instanceConfig[resolvedFlowInstanceId].imageGallery
                                : {}
                            const gallery = buildImageGalleryFromInputs(inputs, config, galleryOverrides)
                            const selectionIds = (gallery.sourceIds?.length ? gallery.sourceIds : inputs.map(d=>d.id)).filter(Boolean)
                            const spacingValue = config.image_spacing ?? config.imageSpacing
                            const columnValue = config.image_columns ?? config.imageColumns
                            const aspectValue = config.image_aspect ?? config.imageAspect
                            const showCaptionsValue = config.image_showCaptions ?? config.imageShowCaptions
                            const fitModeValue = config.image_fitMode ?? config.image_fit ?? config.imageFit ?? "normal"
                            const paddingValue = config.image_padding ?? config.imagePadding
                            const resolvedFitOptions = resolveImageExpansionOptions(fitModeValue)
                            const imageObject = {
                                type: "image_gallery",
                                images: gallery.images,
                                ids: selectionIds,
                                totalImages: gallery.total,
                                offset: gallery.offset,
                                startIndex: gallery.offset,
                                count: gallery.count,
                                emptyText: config.emptyText ?? "No images available",
                                fromPrimitive: true,
                                imageSourceIds: gallery.sourceIds,
                                skipSourceIds: gallery.skipSourceIds ?? [],
                                flowInstanceId: resolvedFlowInstanceId,
                                imageFit: resolvedFitOptions.fit
                            }
                            if( spacingValue !== undefined ){
                                const parsedSpacing = Number(spacingValue)
                                if( Number.isFinite(parsedSpacing) ){
                                    imageObject.imageSpacing = parsedSpacing
                                }
                            }
                            if( columnValue !== undefined ){
                                const parsedColumns = toInteger(columnValue, 0)
                                if( parsedColumns > 0 ){
                                    imageObject.columns = parsedColumns
                                }
                            }
                            if( aspectValue !== undefined ){
                                const parsedAspect = Number(aspectValue)
                                if( Number.isFinite(parsedAspect) && parsedAspect > 0 ){
                                    imageObject.aspectRatio = parsedAspect
                                }
                            }
                            if( showCaptionsValue !== undefined ){
                                imageObject.showCaptions = Boolean(showCaptionsValue)
                            }
                            if( paddingValue !== undefined ){
                                imageObject.padding = paddingValue
                            }
                            myState[stateId].object = imageObject
                            myState[stateId].config = "plain_object"
                            myState[stateId].primitive = basePrimitive
                            myState[stateId].primitiveList = inputs
                            myState[stateId].empty = gallery.total === 0
                            myState[stateId].noContent = gallery.total === 0
                            delete myState[stateId].errors
                            delete myState[stateId].errorMessage
                            renderType = "plain_object"
                            didChange = true
                        }else if( basePrimitive.getConfig.extract === "content"){
                            const data = []
                            let contentType = "text"
                            const baseSectionConfig = basePrimitive.referenceParameters?.sections ?? {}
                            let resolvedSectionConfig = baseSectionConfig
                            for(const input of inputs){
                                if( input.type === "summary" || input.referenceParameters?.structured_summary){
                                    contentType = "structured_text"
                                    const { template: structureTemplate } = resolveSummaryStructureTemplate(basePrimitive);
                                    const parentSegment = input.parentPrimitives.find(d=>d.type === "segment")
                                    const { sections: formattedSections, sectionConfig: mergedConfig } = prepareStructuredSummary({
                                        structuredSummary: input.referenceParameters.structured_summary,
                                        sectionConfig: resolvedSectionConfig ?? baseSectionConfig,
                                        segmentTitle: parentSegment?.filterDescription,
                                        summaryFallback: input.referenceParameters.summary ?? "",
                                        structureTemplate,
                                        includeSegmentTitle: true
                                    })
                                    if( mergedConfig ){
                                        resolvedSectionConfig = mergedConfig
                                    }
                                    data.push( formattedSections )
                                }else{
                                    let text
                                    if( input.type === "segment"){
                                        text = input.filterDescription
                                    }else{
                                        const field = basePrimitive?.renderConfig?.field ?? basePrimitive?.getConfig?.field
                                        text = field ? ( field === "context" ? input.content : input.referenceParameters[field]) : input.title
                                        if(field && input.metadata?.parameters[field] ){
                                            if( input.metadata?.parameters[field].type === "number" ){
                                                text = formatNumber( text )
                                            }
                                        }
                                        format.fromPrimitive = true
                                    }
                                    data.push( text )
                                }
                            }

                            myState[stateId].object = {
                                type: contentType,
                                ids: contentType === "structured_text"
                                    ? [...new Set(data.flat().flatMap(s => s?.ids || []))]
                                    : inputs.map(d=>d.id),
                                text: data,
                                sectionConfig: contentType === "structured_text" ? resolvedSectionConfig : undefined,
                                ...format
                            } 
                            myState[stateId].config = "plain_object"
                            myState[stateId].primitive = basePrimitive
                            myState[stateId].primitiveList = inputs
                            myState[stateId].empty = !config.flowinstance && (!inputs || inputs.length === 0 )
                            myState[stateId].noContent = !config.flowinstance && (!myState[stateId].empty  && (data.length === 0 || data.flat().every(d=>d?.content === "")))

                            const nestedErrors = inputs.flatMap(d=>Object.values(d.processing ?? {}).filter(d=>d.status=="error").map(e=>({error: true, primitive: d, details:e.error})))
                            const errors = Object.values(nestedErrors.reduce((a, d)=>{
                                const o = d.primitive.origin
                                if( !a[o.id]){
                                    let error 
                                    const flowDetails = o.processing?.flow
                                    if( flowDetails?.status == "error" || flowDetails?.error){
                                        error = flowDetails.error
                                    }
                                    a[o.id] = {
                                        primitive: o,
                                        children: [d],
                                        error
                                    }
                                }else{
                                    a[o.id].children.push(d)
                                }
                                return a
                            }, {}))
                            const hasErrors = errors.length > 0;
                            myState[stateId].errors = hasErrors ? errors : undefined;
                            if (hasErrors) {
                                const messageFromEntries = errors.map(entry => {
                                    let errorMessage = entry?.primitive?.getConfig?.fcErrorMessage
                                    if( errorMessage?.includes("{")){
                                        Object.entries(entry.primitive.inputs).forEach(([k,v])=>{
                                            if( v?.data ){
                                                errorMessage = errorMessage.replaceAll(`{${k}}`, v.data)
                                            }
                                        })
                                    }
                                    return errorMessage
                                }).find(Boolean);
                                const fallbackMessage = basePrimitive?.getConfig?.fcErrorMessage ?? pageState?.primitive?.getConfig?.fcErrorMessage ?? pageInstance?.getConfig?.fcErrorMessage;
                                myState[stateId].errorMessage = messageFromEntries ?? fallbackMessage ?? myState[stateId].errorMessage;
                            } else {
                                delete myState[stateId].errorMessage;
                            }

                            renderType = "plain_object"
                        }else if (basePrimitive.getConfig.extract === "page"){
                            let text = format.compose
                            format.compose = undefined
                            if( text && pagePrimitive){
                                if( text.match(/\{.+\}/)){
                                    const status = pageInstance.inputPinsWithStatus
                                    const pinMap = PrimitiveConfig.getPinMap(pageInstance, "inputs")
                                    const expansionItems = text.match(/\{([^}]+)\}/g).map(d=>d.slice(1,-1)).filter((d,i,a)=>a.indexOf(d)===i);
                                    const pinData = {}
                                    myState[stateId].pinSource = []
                                    for(const expFull of expansionItems){
                                        const [exp, mod] = expFull.split("_")
                                        const pin = Object.keys(status).find(d=>status[d].name === exp)
                                        if( pin ){
                                            if( status[pin].connected ){
                                                const pinConnectDetails = pinMap.find(d=>d.inputPin === pin)
                                                if( pinConnectDetails ){

                                                    console.log(`---> Will get pin data src = ${ pinConnectDetails.sourceId}`)

                                                    let sourcePrim = MainStore().primitive(pinConnectDetails.sourceId)
                                                    myState[stateId].pinSource.push(sourcePrim)
                                                    if( sourcePrim.inFlow && sourcePrim.configParent){
                                                        sourcePrim = sourcePrim.configParent
                                                    }
                                                    const source = myState[ sourcePrim.id ]
                                                    let localItems = []
                                                    if( source ){
                                                        if( source.primitiveList){
                                                            localItems = source.primitiveList
                                                        }else{
                                                            localItems = source.list?.map(d=>d.primitive)
                                                        }
                                                    }
                                                    pinData[exp] = {data: localItems}
                                                }
                                            }
                                        }
                                    }
                                    text = expandStringLiterals( text, pinData)
                                }
                            }                    
                            myState[stateId].object = {
                                type: "text",
                                text: text,
                                ...format
                            } 
                            myState[stateId].config = "plain_object"
                            myState[stateId].primitive = basePrimitive
                            renderType = "plain_object"
                            didChange = true
                        }else{
                            myState[stateId].primitiveList = inputs
                            myState[stateId].empty = !inputs || inputs.length === 0
                            if( config.view_axis ){
                                const status = pageInstance.inputPinsWithStatus?.[pins[0]]
                                if( status?.connected ){
                                    const map = PrimitiveConfig.getPinMap(pageInstance, "inputs")
                                    const pin = map.find(d=>d.inputPin === pins[0])
                                    if( pin ){
                                        myState[stateId].axisSource = mainstore.primitive(pin.sourceId)
                                    }
                                }
                            }
                            renderType = "view"
                        }
                    
                    }else{
                        const resolvedSourcePrimitives = uniquePrimitives(
                            (sources ?? [])
                                .map((source)=>typeof source === "string" ? mainstore.primitive(source) : source)
                                .filter((source)=>source && source.id)
                        )
                        // DSL summaries (categoryId 182) have a custom slide
                        // renderer registered against categoryMaps[182]. When
                        // the pin resolves as non-"primitive" config the inputs
                        // here are extracted text (referenceParameters.summary,
                        // i.e. the "Crystallized at ..." timestamp) — rendering
                        // those as plain text bypasses the slide renderer.
                        // Route to the categoryId pipeline by using the source
                        // primitives as primitiveList and the "view" renderType.
                        const dslSources = resolvedSourcePrimitives.filter(d=>d.referenceId === 182)
                        if( dslSources.length > 0 && dslSources.length === resolvedSourcePrimitives.length ){
                            myState[stateId].primitiveList = resolvedSourcePrimitives
                            myState[stateId].empty = resolvedSourcePrimitives.length === 0
                            renderType = "view"
                        }else{
                            myState[stateId].object = {
                                type: "text",
                                ids: resolvedSourcePrimitives.map(d=>d.id),
                                fromPrimitive: true,
                                text: inputs,
                                ...format
                            }
                            myState[stateId].config = "plain_object"
                            myState[stateId].primitive = basePrimitive
                            if( basePrimitive.getConfig.extract !== "page" && resolvedSourcePrimitives.length > 0 ){
                                myState[stateId].primitiveList = resolvedSourcePrimitives
                            }
                            myState[stateId].empty = !inputs || inputs.length === 0
                            renderType = "plain_object"
                        }
                    }
                }

                didChange = true

            }

        }
        
        if( myState[stateId].config === "plain_object" ){
            const renderContextId = `${stateId}`;
            myState.renderContexts ||= new Map();
            let sourceList = myState[stateId].primitiveList;
            if ((!sourceList || sourceList.length === 0) && Array.isArray(myState[stateId].object?.ids)) {
                sourceList = myState[stateId].object.ids
                    .map(id => mainstore.primitive(id))
                    .filter(Boolean);
            }
            const directSourceList = uniquePrimitives((sourceList ?? []).filter(Boolean));
            const firstLevelSourceList = expandInspectableSourceListFirstLevel(directSourceList);
            const expandedSourceList = expandInspectableSourceList(directSourceList);
            const inspectableSourceList = expandedSourceList?.length ? expandedSourceList : directSourceList;
            if (inspectableSourceList?.length) {
                myState.renderContexts.set(renderContextId, {
                    primitive: basePrimitive,
                    plainObject: myState[stateId].object,
                    primitiveList: inspectableSourceList,
                    directPrimitiveList: directSourceList,
                    firstLevelPrimitiveList: firstLevelSourceList,
                    sourcePrimitiveIds: inspectableSourceList.map(item => item.id)
                });
                myState[stateId].renderContextId = renderContextId;
            }
        }

        myState[stateId].isBoard = true
        const oldConfig = myState[stateId]?.config

        myState[stateId].outputPins = undefined
        myState[stateId].inputPins = undefined
        preparePins( primitiveToPrepare, basePrimitive, stateId, myState)
       

        let widgetConfig = {}
        const showItems = basePrimitive.findParentPrimitives({type: basePrimitive.inFlow ? "flow" : "board"})[0]?.frames?.[stateId]?.showItems
        
        if( !myState.hideWidgets){

            if( primitiveToPrepare.type=== "query"){
                let useQuery = basePrimitive.referenceId === 81  

                widgetConfig.showItems = showItems
                widgetConfig.title = basePrimitive.title
                widgetConfig.icon = <HeroIcon icon='FARobot'/>
                widgetConfig.items = "results"
                widgetConfig.count = primitiveToPrepare.itemsForProcessing.length
                // Prompt may be a DSL JSON object on dsl_transformer queries —
                // coerce to string before slicing so .slice() is always valid.
                const rawSource = useQuery ? primitiveToPrepare.getConfig.query : primitiveToPrepare.getConfig.prompt
                const sourceText = typeof rawSource === "string" ? rawSource : (rawSource != null ? JSON.stringify(rawSource) : "")
                widgetConfig.content = `**${useQuery ? "Query" : "Prompt"}:** ` + sourceText.slice(0, 900)
                myState[stateId].widgetConfig = widgetConfig
                didChange = true
            }else if( primitiveToPrepare.type=== "external"){
                widgetConfig.showItems = false
                widgetConfig.allowShowItems = false
                widgetConfig.title = basePrimitive.title
                widgetConfig.imageUrl = "/api/companyLogo?domain=airtable.com"
                widgetConfig.items = "results"
                widgetConfig.content = `**Result:** ` + (primitiveToPrepare.getConfig.result ?? "")
                myState[stateId].widgetConfig = widgetConfig
                didChange = true
            }else if( primitiveToPrepare.type=== "action"){
                widgetConfig.showItems = showItems
                widgetConfig.title = basePrimitive.title
                widgetConfig.icon = <HeroIcon icon='FARobot'/>
                widgetConfig.items = "results"
                widgetConfig.content = `**Result:** ` + (primitiveToPrepare.getConfig.result ?? "")
                myState[stateId].widgetConfig = widgetConfig
                didChange = true
            }else if( primitiveToPrepare.type=== "summary"){
                widgetConfig.showItems = showItems
                widgetConfig.title = basePrimitive.title
                widgetConfig.icon = <HeroIcon icon='FARobot'/>
                widgetConfig.count = primitiveToPrepare.itemsForProcessing.length
                widgetConfig.items = "results"
                widgetConfig.content = `**Prompt:** ` + primitiveToPrepare.getConfig.prompt
                myState[stateId].widgetConfig = widgetConfig
            }else if( primitiveToPrepare.type=== "question"){
                widgetConfig.showItems = showItems
                widgetConfig.title = basePrimitive.title
                widgetConfig.icon = <HeroIcon icon='QuestionMarkCircleIcon'/>
                widgetConfig.count = primitiveToPrepare.itemsForProcessing.length
                widgetConfig.items = "prompts"
                const promptCount = (primitiveToPrepare.primitives?.origin?.allIds ?? []).length
                widgetConfig.content = `**${promptCount} prompt${promptCount !== 1 ? 's' : ''}**`
                myState[stateId].widgetConfig = widgetConfig
                didChange = true
            }
        }

        

        if( renderType === "widget" || renderType === "flowcontrol"){
            myState[stateId].primitive = basePrimitive
            myState[stateId].config = "widget"
            let renderData = {}

            myState[stateId].renderData = renderData
        }else if( renderType === "view" || renderType === "query" || renderType === "observation" || (renderType === "action" && primitiveToPrepare.metadata.hasResults)){
            
            //const items = myState[stateId].primitiveList ?? primitiveToPrepare.itemsForProcessing
            const items = getLocalPrimitiveListForBoardId(stateId, myState) ?? primitiveToPrepare.itemsForProcessing
            const basePrimitiveConfig = typeof basePrimitive?.getConfig === "function"
                ? basePrimitive.getConfig()
                : basePrimitive?.getConfig;
            const meta = items.length > 0 ? items?.[0]?.metadata : mainstore.category(basePrimitiveConfig?.referenceId)
            
            const axisSourcePrimitive = myState[stateId]?.axisSource
            const axisSourceOwner = axisSourcePrimitive?.configParent ?? axisSourcePrimitive
            const datasetViewOwner =
                (axisSourceOwner?.referenceParameters?.datasetView?.enabled ? axisSourceOwner : null)
                ?? (axisSourcePrimitive?.referenceParameters?.datasetView?.enabled ? axisSourcePrimitive : null)
            const viewConfigOwner = datasetViewOwner ?? basePrimitive
            const viewConfigs = CollectionUtils.resolveViewConfigsForPrimitive(viewConfigOwner, meta)
            const viewOwnerConfig = typeof viewConfigOwner?.getConfig === "function"
                ? viewConfigOwner.getConfig()
                : viewConfigOwner?.getConfig;
            const viewMatch = CollectionUtils.matchViewConfig(viewConfigs, viewOwnerConfig?.explore?.view)
            let activeView = viewMatch.index
            let viewConfig = viewMatch.config ?? viewConfigs[0] 
            if( forceViewConfig ){
                activeView = viewConfigs.findIndex(d=>d.renderType === forceViewConfig.viewConfig || d.id === forceViewConfig.viewConfig) 
                if( activeView == -1){
                    viewConfig = {
                        renderType: forceViewConfig.viewConfig
                    }
                }else{
                    viewConfig = viewConfigs[activeView] 
                }
            }

            const columnAxis = CollectionUtils.primitiveAxis(primitiveToPrepare, "column", items)
            const rowAxis = CollectionUtils.primitiveAxis(primitiveToPrepare, "row", items)

            
            
            if( viewConfig?.renderType === "cat_overview"){
                let config = primitiveToPrepare.getConfig
                let categoriesToMap = []
                if( config.view_axis ){
                    let dataSource
                    if( myState[stateId].axisSource.inFlow && myState[stateId].axisSource.configParent?.flowElement){
                        dataSource = myState[myState[stateId].axisSource.configParent.id] 
                    }else{
                        dataSource = myState[myState[stateId].axisSource.id] 
                    }
                    if( dataSource ){
                        categoriesToMap = [
                            dataSource.axis?.row?.type === "category" ?  dataSource.axis?.row?.primitiveId : undefined,
                            dataSource.axis?.column?.type === "category" ?  dataSource.axis?.column?.primitiveId : undefined,
                        ].filter(d=>d).map(d=>mainstore.primitive(d))

                    }

                }else if( config.explore.axis?.column?.type === "category" || config.explore.axis?.row?.type === "category"){
                    categoriesToMap = [
                        config.explore.axis?.column?.type === "category" ? primitiveToPrepare.primitives.axis.column.allItems : undefined,
                        config.explore.axis?.row?.type === "category" ? primitiveToPrepare.primitives.axis.row.allItems : undefined,
                    ].flat().filter(d=>d)
                }else{
                    categoriesToMap = primitiveToPrepare.primitives.origin.allUniqueCategory
                }             
                console.log(`Got ${categoriesToMap?.length} to map`)
                let mappedCategories = categoriesToMap.map(category=>{
                    console.log(`Doing ${category.title} - ${category.primitives.allUniqueCategory.length} subcats`)
                    const axis = {
                        type: "category",
                        title: category.title,
                        access: category.referenceParameters.access,
                        primitiveId: category.id,
                        relationship: category.referenceParameters.relationship
                    }
                    let {data, extents} = CollectionUtils.mapCollectionByAxis( items, axis, undefined, [], [], undefined )
                    //console.log(data, extents)
                    let columnsToInclude, totalCount = data.length
                    if( primitiveToPrepare.renderConfig?.show_none ){
                        const none = extents.column.find(d=>d.idx === "_N_")
                        columnsToInclude =  [none, ...extents.column.filter(d=>d.idx !== "_N_")]
                    }else{
                        totalCount -= data.filter(d=>d.column === "_N_").length
                        columnsToInclude =  extents.column.filter(d=>d.idx !== "_N_")
                    } 

                    return {
                            id: category.id,
                            title: `${category.title} (${totalCount})`,
                            //details: extents.column.filter(d=>d.idx !== "_N_").map(d=>{
                            details: columnsToInclude.map(d=>{
                                const items = data.filter(d2=>d2.column === d.idx || (Array.isArray(d2.column) && d2.column.includes(d.idx)))
                                return {
                                    idx: d.idx,
                                    label: d.label,
                                    tag: mainstore.primitive(d.idx)?.referenceParameters?.tag,
                                    count: items.length,
                                    items
                                }})
                        }
                })

                delete myState[stateId]["data"]
                myState[stateId].primitive = basePrimitive
                myState[stateId].stateId = stateId
                myState[stateId].config = viewConfig.configName ?? "cat_overview"
                myState[stateId].renderData = {
                    mappedCategories
                }
            }else{
                if( viewConfig.matrixType === "timeseries" || viewConfig.matrixType === "checktable" || viewConfig.matrixType === "ranking" || viewConfig.matrixType === "xy_cluster" || viewConfig.matrixType === "overunder" || viewConfig.matrixType === "distribution" || viewConfig.matrixType === "series" || viewConfig.showAsCounts || viewConfig.matrixType === undefined){
                    let dataTable 

                    function setupDataConfig( viewConfig, renderConfig = {} ){
                        const baseConfig = viewConfig?.dataConfig ? {...viewConfig.dataConfig} : {}
                        const config = baseConfig
                        if( config?.timeseries ){
                            const normalizedTimeseries = {...config.timeseries}
                            normalizedTimeseries.delta = primitiveToPrepare.renderConfig?.timeRange === "delta_row" || primitiveToPrepare.renderConfig?.timeRange === "delta_column"
                            normalizedTimeseries.limitTicks = primitiveToPrepare.renderConfig?.cap
                            normalizedTimeseries.cumulative = primitiveToPrepare.renderConfig?.cumulative
                            normalizedTimeseries.resolution = primitiveToPrepare.renderConfig?.resolution
                            normalizedTimeseries.rangeAnchor = primitiveToPrepare.renderConfig?.timeseries_anchor ?? "now"
                            const { startDate, endDate, durationMs } = deriveTimeseriesRange(renderConfig)
                            if( startDate ){
                                normalizedTimeseries.startDate = startDate
                            }else if( renderConfig.start ){
                                normalizedTimeseries.startDate = renderConfig.start
                            }
                            if( endDate ){
                                normalizedTimeseries.endDate = endDate
                            }else if( renderConfig.end ){
                                normalizedTimeseries.endDate = renderConfig.end
                            }
                            if( durationMs ){
                                normalizedTimeseries.rangeDurationMs = durationMs
                            }
                            config.timeseries = normalizedTimeseries
                        }
                        if( renderConfig.count ){
                            config.limitItems = renderConfig.count
                        }
                        return config
                    }

                    function deriveTimeseriesRange(renderConfig = {}){
                        const rangeValue = renderConfig.range
                        if( rangeValue === undefined || rangeValue === null || rangeValue === "" ){
                            return {}
                        }
                        const endDate = renderConfig.end ? new Date(renderConfig.end) : new Date()
                        if( Number.isNaN(endDate.getTime()) ){
                            return {}
                        }
                        const parsed = parseRangeValue(rangeValue)
                        if( !parsed || parsed <= 0 ){
                            return { durationMs: parsed }
                        }
                        const startDate = new Date(endDate.getTime() - parsed)
                        return {
                            startDate: startDate.toISOString(),
                            endDate: endDate.toISOString(),
                            durationMs: parsed
                        }
                    }

                    function parseRangeValue(value){
                        const MS_IN_DAY = 24 * 60 * 60 * 1000
                        if( typeof value === "number" && Number.isFinite(value) ){
                            return value > 0 ? value * 365 * MS_IN_DAY : null
                        }
                        if( typeof value !== "string" ){
                            return null
                        }
                        const trimmed = value.trim().toLowerCase()
                        if( !trimmed ){
                            return null
                        }
                        if( /^\d+$/.test(trimmed) ){
                            const years = Number.parseInt(trimmed, 10)
                            return years > 0 ? years * 365 * MS_IN_DAY : null
                        }
                        const match = trimmed.match(/^(\d+(?:\.\d+)?)\s*(year|yr|y|month|mo|week|wk|w|day|d)s?$/)
                        if( !match ){
                            return null
                        }
                        const amount = Number.parseFloat(match[1])
                        if( !Number.isFinite(amount) || amount <= 0 ){
                            return null
                        }
                        const unit = match[2]
                        switch(unit){
                            case "year":
                            case "yr":
                            case "y":
                                return amount * 365 * MS_IN_DAY
                            case "month":
                            case "mo":
                                if( amount === 1 ){
                                    return 4 * 7 * MS_IN_DAY
                                }
                                return amount * 30 * MS_IN_DAY
                            case "week":
                            case "wk":
                            case "w":
                                return amount * 7 * MS_IN_DAY
                            case "day":
                            case "d":
                                return amount * MS_IN_DAY
                            default:
                                return null
                        }
                    }
                    
                    if(myState[stateId].axisSource){
                        let dataSource
                        if (myState[stateId].axisSource.inFlow && myState[stateId].axisSource.configParent?.flowElement) {
                            dataSource = myState[myState[stateId].axisSource.configParent.id];
                        } else {
                            dataSource = myState[myState[stateId].axisSource.id];
                        }
                        if( dataSource){
                            const axisSourcePrimitive = myState[stateId].axisSource
                            const axisSourceOwner = axisSourcePrimitive?.configParent ?? axisSourcePrimitive
                            const dataSourceOwner = dataSource?.primitive?.configParent ?? dataSource?.primitive
                            const datasetConfigOwner =
                                (axisSourcePrimitive?.referenceParameters?.datasetView?.enabled ? axisSourcePrimitive : null)
                                ?? (axisSourceOwner?.referenceParameters?.datasetView?.enabled ? axisSourceOwner : null)
                                ?? (dataSourceOwner?.referenceParameters?.datasetView?.enabled ? dataSourceOwner : null)
                                ?? axisSourceOwner
                            const datasetView = datasetConfigOwner?.referenceParameters?.datasetView
                            if( datasetView?.enabled ){
                                let datasetOverride = datasetView
                                if( axisSourcePrimitive?.inFlow && axisSourcePrimitive?.configParent?.flowElement ){
                                    datasetOverride = {
                                        ...datasetView,
                                        sourceInstanceId: datasetView.sourceInstanceId ?? axisSourcePrimitive.id
                                    }
                                }
                                const datasetBuildOwner = (axisSourcePrimitive?.inFlow && axisSourcePrimitive?.configParent?.flowElement)
                                    ? axisSourcePrimitive
                                    : datasetConfigOwner
                                dataTable = CollectionUtils.createDataTableFromDatasetView(
                                    datasetBuildOwner,
                                    datasetOverride,
                                    setupDataConfig(dataSource.viewConfig ?? viewConfig, dataSource.renderConfig ?? primitiveToPrepare.renderConfig)
                                )
                                if( !dataTable ){
                                    dataTable = CollectionUtils.createDataTableForPrimitive(
                                        datasetBuildOwner,
                                        setupDataConfig(dataSource.viewConfig ?? viewConfig, dataSource.renderConfig ?? primitiveToPrepare.renderConfig),
                                        items
                                    )
                                }
                            }
                            if( dataTable ){
                                myState[stateId].primitive = basePrimitive
                                myState[stateId].viewConfig = viewConfig
                                myState[stateId].data = dataTable
                                myState[stateId].internalWatchIds = dataTable.ids
                                const renderContextId = `${stateId}`;
                                myState.renderContexts ||= new Map();
                                myState.renderContexts.set(renderContextId, {
                                    dataTable,
                                    primitive: basePrimitive,
                                    viewConfig,
                                });
                                myState[stateId].renderContextId = renderContextId;
                                if( renderType === "observation"){
                                    const refParams = primitiveToPrepare.referenceParameters ?? {}
                                    myState[stateId].renderData = {
                                        tableData: dataTable,
                                        viewConfig,
                                        takeaway: refParams.takeaway,
                                        description: refParams.description,
                                        summaryInclude: refParams.summaryInclude,
                                        summaryComparison: refParams.summaryComparison
                                    }
                                    myState[stateId].config = "observation"
                                }else{
                                    myState[stateId].config = "datatable"
                                }
                                return [stateId]
                            }
                            let columns = (dataSource.data ? dataSource.data.defs?.columns : dataSource.axis?.column ) ?? {}
                            let rows = (dataSource.data ? dataSource.data.defs?.rows : dataSource.axis?.row) ?? {}
                            let viewFilters = (dataSource.data ? dataSource.data.defs?.viewFilters : undefined) ?? []
                            let hideNull = dataSource.data ? dataSource.data.defs?.hideNull : dataSource.hideNull
                            if( viewConfig.matrixType === "distribution" && dataSource.inFlow && viewFilters.length === 0 && rows){
                                viewFilters = [rows]
                                rows = {}
                            }
                            dataTable = CollectionUtils.createDataTable( items, {columns, rows, viewFilters, config: setupDataConfig(dataSource.viewConfig, dataSource.renderConfig), hideNull, alreadyFiltered: true, cacheId: dataSource?.id ?? primitiveToPrepare?.id})
                        }

                    }
                    if( !dataTable ){
                        dataTable = CollectionUtils.createDataTableForPrimitive( primitiveToPrepare, setupDataConfig(viewConfig, primitiveToPrepare.renderConfig), items )
                    }

                    myState[stateId].primitive = basePrimitive
                    myState[stateId].viewConfig = viewConfig
                    myState[stateId].data = dataTable
                    myState[stateId].internalWatchIds = dataTable.ids
                    const renderContextId = `${stateId}`;
                    myState.renderContexts ||= new Map();
                    myState.renderContexts.set(renderContextId, {
                        dataTable,
                        primitive: basePrimitive,
                        viewConfig,
                    });
                    myState[stateId].renderContextId = renderContextId;

                    if( renderType === "observation"){
                        const refParams = primitiveToPrepare.referenceParameters ?? {}
                        myState[stateId].renderData = {
                            tableData: dataTable,
                            viewConfig,
                            takeaway: refParams.takeaway,
                            description: refParams.description,
                            summaryInclude: refParams.summaryInclude,
                            summaryComparison: refParams.summaryComparison
                        }
                        myState[stateId].config = "observation"
                    }else{
                        myState[stateId].config = "datatable"
                    }

                    return [stateId]
                }                    
                columnAxis.allowMove = columnAxis.access === 0 && !columnAxis.relationship
                rowAxis.allowMove = rowAxis.access === 0 && !rowAxis.relationship

                const primitiveConfig = primitiveToPrepare.getConfig
                let hideNull = primitiveConfig?.explore?.hideNull
                let viewPivot = primitiveConfig?.explore?.viewPivot
                let viewFilters = []
                if( viewConfig.needsAllAllocations ){
                    viewFilters = primitiveConfig?.explore?.filters?.map((d2,i)=>CollectionUtils.primitiveAxis(primitiveToPrepare, i, items)) ?? []            
                }
                let filterApplyColumns = primitiveConfig?.explore?.axis?.column?.filter ?? []
                let filterApplyRows = primitiveConfig?.explore?.axis?.row?.filter ?? []

                let liveFilters = primitiveToPrepare.primitives.allUniqueCategory.filter(d=>primitiveToPrepare.referenceId === PrimitiveConfig.Constants["LIVE_FILTER"]).map(d=>{
                    return {
                        type: "category",
                        primitiveId: d.id,
                        category: d,
                        isLive: true,
                        title: `Category: ${d.title}`                
                    }
                })
                
                let {data, extents} = CollectionUtils.mapCollectionByAxis( items, columnAxis, rowAxis, viewFilters, liveFilters, viewPivot )
                const filteredColumnExtents = extents.column.filter(d=>{
                    if( d.idx === "_N_" && (filterApplyColumns.includes(undefined) || filterApplyColumns.includes(null))){
                        return false
                    }
                    if( filterApplyColumns.includes(d.idx)){
                        return false
                    }
                    return true
                    
                })
                const filteredRowExtents = extents.row.filter(d=>{
                    if( d.idx === "_N_" && (filterApplyRows.includes(undefined) || filterApplyRows.includes(null))){
                        return false
                    }
                    if( filterApplyRows.includes(d.idx)){
                        return false
                    }
                    return true
                    
                })
                const filteredAllocations = viewFilters.reduce((a,c,i)=>{
                    const name = `filterGroup${i}`
                    a[name] = extents[name].filter(d=>{
                        
                        if( d.idx === "_N_" && (c.filter.includes(undefined) || c.filter.includes(null))){
                            return false
                        }
                        if( c.filter.includes(d.idx)){
                            return false
                        }
                        return true
                    })
                    return a
                }, {})

                let filtered = CollectionUtils.filterCollectionAndAxis( data, [], {columns: filteredColumnExtents, rows: filteredRowExtents, hideNull})

                if( myState[stateId].list ){
                    if( filtered.data.length !== myState[stateId].list.length){
                        didChange = true
                    }else{
                        const changes = myState[stateId].list.some((d,i)=>{
                            const n = filtered.data[i]
                            if( !n ){
                                return true
                            }
                            if( n?.primitive?.id !== d.primitive?.id ){
                                return true
                            }
                            if( ([n.column].flat()).map(d=>d?.idx ?? d).join("-") != ([d.column].flat()).map(d=>d?.idx ?? d).join("-")){
                                return true
                            }
                            if( ([n.row].flat()).map(d=>d?.idx ?? d).join("-") != ([d.row].flat()).map(d=>d?.idx ?? d).join("-")){
                                return true
                            }
                            return false
                        })
                        didChange = didChange || changes
                    }
                }

                const watchIds = new Set( filtered.data.flatMap(d=>d.primitive.parentPrimitiveIds) )
                            
                console.warn(`DEPRECATED renderMatrix for ${viewConfig.configName} / ${viewConfig.title} / ${viewConfig.matrixType}`)
                myState[stateId].primitive = basePrimitive
                myState[stateId].config = viewConfig.configName ?? "explore_" + activeView
                myState[stateId].list = filtered.data
                myState[stateId].internalWatchIds = Array.from(watchIds)
                myState[stateId].axis = {column: columnAxis, row: rowAxis}
                myState[stateId].columns = filtered.columns
                myState[stateId].viewConfig = viewConfig
                myState[stateId].rows = filtered.rows
                myState[stateId].hideNull = hideNull
                myState[stateId].extents = extents
                myState[stateId].filteredAllocations = filteredAllocations
                myState[stateId].toggles = Object.keys(extents).reduce((a,c)=>{
                                                                        if(c.match(/liveFilter/)){
                                                                            a[c] = extents[c]
                                                                        }
                                                                        return a}, {})
                if( viewConfig?.renderType === "summary_section"){
                    const sources = []
                    const track = new Set()
                    function expandSources(d){
                        if( Object.keys(d.primitives ?? {}).includes("source")){
                            for(const d2 of d.primitives.source.allItems){
                                if( !track.has(d2.id)){
                                    track.add(d2.id)
                                    sources.push(d2)
                                    expandSources(d2)
                                }
                            }
                        }
                    }
                    items.forEach(d=>expandSources(d))
                    const company_candidates = MainStore().uniquePrimitives( sources.flatMap(d=>d.referenceId === 29 ? d : d.findParentPrimitives({referenceId: [29], first: true}))).flat(Infinity) 
                    myState[stateId].renderData = {
                        company_candidates,
                        ...(basePrimitive.renderConfig ?? {})
                    }
                }
            }
        }else if( renderType === "result" || renderType === "summary" || renderType === "element" || renderType === "action" || renderType === "external"){
            let viewConfig
            const viewConfigs = CollectionUtils.resolveViewConfigsForPrimitive(basePrimitive, basePrimitive.metadata)
            if( forceViewConfig ){
                let activeView = viewConfigs.findIndex(d=>d.renderType === forceViewConfig.viewConfig || d.id === forceViewConfig.viewConfig) 
                if( activeView == -1){
                    viewConfig = {
                        renderType: forceViewConfig.viewConfig
                    }
                }else{
                    viewConfig = viewConfigs[activeView] 
                }
            }

            let childChanged = myState[stateId].showItems !== showItems
            myState[stateId].showItems = showItems
            myState[stateId].primitive = basePrimitive
            myState[stateId].list = [{column: undefined, row: undefined, primitive: primitiveToPrepare}]
            myState[stateId].columns = [{idx: undefined, label: ''}]
            myState[stateId].rows = [{idx: undefined, label: ''}]
            myState[stateId].viewConfig = viewConfig
            myState[stateId].config = "full"
            myState[stateId].extents = {
                columns: [{idx: undefined, label: ''}],
                row:[{idx: undefined, label: ''}]
            }
            myState[stateId].toggles = {}
            
            didChange ||= childChanged
        }else if( renderType === "actionrunner" || renderType === "categorizer" ){
            const items = primitiveToPrepare.itemsForProcessing
            const title = items.length === 0 ? "items" : (items.length > 1 ? items[0]?.metadata?.plural : undefined ) ?? items[0]?.metadata?.title
            myState[stateId].primitive = basePrimitive
            myState[stateId].config = "widget"
            myState[stateId].renderData = {
                title: basePrimitive.title,
                icon: <HeroIcon icon='FARun'/>,
                count: items.length,
                items: items[0]?.metadata?.title ?? "items"
            }
            didChange = true
        }else if( renderType === "search" ){

            const resultCategory = mainstore.category( d.metadata.parameters.sources.options[0].resultCategoryId )

            myState[stateId].primitive = basePrimitive
            myState[stateId].config = "widget"
            myState[stateId].renderData = {
                title: basePrimitive.title,
                icon: <HeroIcon icon={resultCategory?.icon}/>,
                items: resultCategory.plural ?? resultCategory.title + "s",
                //count: primitiveToPrepare.primitives.strictDescendants.filter(d=>d.referenceId === resultCategory.id).length
            }
        }else if( renderType === "question" ){
            const prompts = primitiveToPrepare.primitives?.origin?.allItems ?? []
            const importCount = primitiveToPrepare.primitives?.imports?.allIds?.length ?? 0
            myState[stateId].primitive = basePrimitive
            myState[stateId].config = "widget"
            myState[stateId].renderData = {
                title: basePrimitive.title,
                icon: <HeroIcon icon='QuestionMarkCircleIcon'/>,
                items: `${prompts.length} prompt${prompts.length !== 1 ? 's' : ''}`,
                count: prompts.length,
                status: importCount > 0 ? `${importCount} docs` : undefined,
            }
            didChange = true
        }else if( renderType === "page" ){
            let childNodes = d.primitives.origin.uniqueAllItems
            childNodes = [...childNodes.filter(d=>d.referenceId === PrimitiveConfig.Constants.SHAPE_ELEMENT && d.referenceParameters.style === "rect"), ...childNodes.filter(d=>d.referenceId === PrimitiveConfig.Constants.SHAPE_ELEMENT && d.referenceParameters.style !== "rect"), ...childNodes.filter(d=>d.referenceId !== PrimitiveConfig.Constants.SHAPE_ELEMENT )]
            
            didChange ||= d.referenceParameters?.explore?.view !== myState[stateId].lastView
            
            myState[stateId].internalWatchIds = childNodes.map(d=>d.id)

            myState[stateId].primitive = basePrimitive
            myState[stateId].config = "page"
            myState[stateId].lastView = d.referenceParameters?.explore?.view

            const themeKey = d.renderConfig?.theme || "default";
            const theme = themes[themeKey] || themes.default;
            myState[stateId].theme = theme

            if( myState.showSlideSuggestions  || (!myState[stateId].renderSubPages && !RENDERSUB)){
                for(let child of childNodes){
                    myState[child.id] ||= {
                        id: child.id, 
                        inPage: true,
                        theme: theme,
                        page: d
                    }
                    const renderResult = SharedPrepareBoard(child, myState)
                    const childChanged = renderResult !== false
                    if( childChanged ){
                        boardsToRefresh = boardsToRefresh.concat([child.id, ...renderResult])
                    }
                    didChange ||= (childChanged ?? true)
                    myState[child.id].parentRender = stateId
                    if( d.renderConfig?.background ){
                        myState[child.id].backgroundLayer = true
                    }
                }
                if( basePrimitive.slide_state?.data?.slideSpec ){
                    const visuals = basePrimitive.slide_state?.data?.slideSpec.sections//.filter(d=>d.type == "visualization")
                    myState[stateId].renderData = {
                        placeholder: true,
                        sections: visuals.map(d=>{
                            if( d.type == "visualization" && d.chart){
                                return {
                                    visualization: ConvertVisualizationSpec( d,  basePrimitive.slide_state?.data?.slideSpec?.defs)
                                }
                            }
                        })
                    }
                }
            }
        }else if( renderType === "flow" ){
            let childNodes = d.primitives.origin.uniqueAllItems
            childNodes = [...childNodes.filter(d=>d.type !== "page"),...childNodes.filter(d=>d.type === "page")]

            
            const flowInstances = childNodes.filter(child=>{
                if( child.type !== "flowinstance" ){
                    return false
                }
                if( basePrimitive.flowElement){
                    // subflow
                    if( myState[stateId].flowInstance){
                        return myState[stateId].flowInstance.primitives.subfi.allIds.includes( child.id)
                    }
                }
                return true
            }).sort((a,b)=>a.plainId - b.plainId)
            const flowSelection = resolveFlowInstanceSelection(flowInstances, d.referenceParameters?.explore?.view)
            const flowInstanceToShow = flowSelection.instance
            childNodes = childNodes.filter(d=>d.type !== "flowinstance")
            didChange ||= d.referenceParameters?.explore?.view !== myState[stateId].lastView
            
            myState[stateId].primitive = basePrimitive
            myState[stateId].flowInstances = flowInstances
            myState[stateId].config = "flow"
            myState[stateId].lastView = d.referenceParameters?.explore?.view
            myState[stateId].title = `${basePrimitive.title} - #${basePrimitive.plainId}${flowInstanceToShow ? ` (${flowInstanceToShow.plainId})` : ""}`
            myState[stateId].renderData = {
                icon: <HeroIcon icon='CogIcon'/>,
                count: primitiveToPrepare.primitives.uniqueAllIds.length
            }

            for(let child of childNodes){
                if( child.type === "flowinstance"){
                    continue
                }
                const showItems = d.frames?.[child.id]?.showItems
                const parentFlowInstanceChanged = myState[child.id]?.flowInstance?.id !== flowInstanceToShow?.id

                
                log.trace(`- preparing child of flow ${child.plainId} ${child.type}`)
                myState[child.id] ||= {
                    id: child.id, 
                    inFlow: true,
                    flow: d,
                }
                myState[child.id].flowInstance = flowInstanceToShow
                let childChanged = myState[child.id].showItems !== showItems

                myState[child.id].showItems = showItems


                if( flowInstanceToShow && child.type !== "flow"){
                    const instanceChild = flowInstanceToShow.primitives.uniqueAllItems.find(d=>d.parentPrimitiveIds.includes(child.id))
                    if( instanceChild ){
                        myState[child.id].underlying = instanceChild
                    }else{
                        console.log(`-- couldnt find instance for flowinstance ${flowInstanceToShow.id}`)
                    }
                }
                const renderResult = SharedPrepareBoard(child, myState)
                childChanged ||= renderResult !== false || parentFlowInstanceChanged
                if( childChanged ){
                    boardsToRefresh = boardsToRefresh.concat([child.id, ...(renderResult || [])])
                }
                didChange ||= (childChanged ?? true)
                log.trace(`- Change status`, {renderResult: renderResult !== false, parentFlowInstanceChanged, childChanged, didChange})
                myState[child.id].parentRender = stateId
            }
        }
        if( myState[stateId] && forceViewConfig){
            myState[stateId].renderConfigOverride = forceViewConfig.renderConfig
        }
        if( element ){
            myState[stateId].element = element

        }
        if( oldConfig !== myState[stateId].config){
            didChange = true
        }
        return didChange ? [stateId, ...boardsToRefresh] : false
    }



export default function BoardViewer({primitive,...props}){
    const mainstore = MainStore()
    const [manualInputPrompt, setManualInputPrompt] = useState(false)
    const [collectionPaneInfo, setCollectionPaneInfo] = useState(false)
    const canvas = useRef({})
    const myState = useRef({})
    const menu = useRef({})
    const colButton = useRef({})
    const rowButton = useRef({})
    const agentRef = useRef({})
    const [update, forceUpdate] = useReducer( (x)=>x+1, 0)
    const [updateLinks, forceUpdateLinks] = useReducer( (x)=>x+1, 0)

    myState.forceUpdate = forceUpdate
    /*if( myState.current && typeof myState.current === "object" ){
        myState.current.forceUpdate = forceUpdate
    }*/
    const [agentStatus, setAgentStatus] = useState({activeChat: false})
    const [panelTab, setPanelTab] = useState("info")
    const [showAddDrawer, setShowAddDrawer] = useState(false)

    // Publish active board context so extensions can read it
    useEffect(()=>{
        mainstore.activeBoard = primitive
        return ()=>{
            if (mainstore.activeBoard === primitive) {
                delete mainstore.activeBoard
            }
        }
    }, [primitive?.id])

    const computeAllowedDrawerCategories = ()=>{
        
        const allCategories = mainstore.categories()
        const active = myState.activeBoard
        const addToFlow = active?.primitive?.type === "flow" ? active.primitive : undefined
        const addToPage = active?.primitive?.type === "page" ? active.primitive : undefined

        const categoryById = allCategories.reduce((acc, category)=>{
            acc[category.id] = category
            return acc
        }, {})

        if( addToPage ){
            const defaultElementId = PrimitiveConfig.typeConfig?.element?.defaultReferenceId ?? 89
            const addableIds = [
                defaultElementId,
                PrimitiveConfig.Constants.SHAPE_ELEMENT,
                PrimitiveConfig.Constants.IMAGE_ELEMENT
            ]
            return addableIds.map(id=>categoryById[id]).filter(Boolean)
        }

        const baseCategoryIds = [38,81,148,154,130,140,131,142,118,135,136,164,137,132,133,144, 181, 158, 162, 113, 168, 183]
        const ordered = baseCategoryIds.map(id=>categoryById[id]).filter(Boolean)
        const seen = new Set(ordered.map(category=>category.id))

        const appendUnique = (list)=>{
            for(const category of list){
                if(category && !seen.has(category.id)){
                    seen.add(category.id)
                    ordered.push(category)
                }
            }
        }

        appendUnique(allCategories.filter(category=>category.primitiveType === "search"))
        appendUnique(allCategories.filter(category=>category.primitiveType === "external"))

        if( addToFlow ){
            appendUnique([categoryById[81], categoryById[113], categoryById[160]].filter(Boolean))
        }else{
            appendUnique(allCategories.filter(category=>category.primitiveType === "entity"))
        }

        return ordered
    }

    const allowedDrawerCategories = computeAllowedDrawerCategories()
    const allowedCategoryIdSet = new Set(allowedDrawerCategories.map(category=>category.id))
    
    if( primitive.type === "flow" ){
        if( !myState.mainFlowInstances ){
            myState.mainFlowInstances = primitive.primitives.origin.allFlowinstance
        }
        const mainFlowSelection = resolveFlowInstanceSelection(myState.mainFlowInstances, primitive.referenceParameters?.explore?.view)
        myState.mainFlowInstance = mainFlowSelection.instance
    }else if( primitive.type === "flowinstance" ){
        myState.mainFlowInstance = primitive
        if( !myState.mainFlowInstances ){
            myState.mainFlowInstances = [primitive]
        }
    }


    const setCanvasRef = (node) => {
        if (node) {
          canvas.current = node;
          myState.current.canvas = node
          window.canvas = node
        }
      };

    const updateCategoryIndicators = (changedIds = []) => {
        if( !canvas.current?.updateIndicators){
            return
        }
        const idsToCheck = Array.isArray(changedIds) ? changedIds : [changedIds]
        const changed = new Set(idsToCheck.filter(Boolean))
        if( changed.size === 0){
            return
        }
        Object.keys(myState).forEach(frameKey=>{
            if( frameKey === "current"){
                return
            }
            const frameState = myState[frameKey]
            if( !frameState || typeof frameState !== "object"){
                return
            }
            if( !Array.isArray(frameState.categoryScopeIds) || frameState.categoryScopeIds.length === 0){
                return
            }
            if( frameState.categoryScopeIds.some(catId=>changed.has(catId))){
                const builder = frameState.categoryIndicatorBuilder
                if( typeof builder === "function"){
                    const indicators = builder()
                    canvas.current.updateIndicators(frameState.id, indicators)
                }
            }
        })
    }

    const updateActiveChips = (changedIds = []) => {
        if( !canvas.current?.updateActiveChip){
            return
        }
        const idsToCheck = Array.isArray(changedIds) ? changedIds : [changedIds]
        const changed = new Set(idsToCheck.filter(Boolean))
        if( changed.size === 0){
            return
        }
        Object.keys(myState).forEach(frameKey=>{
            if( frameKey === "current"){
                return
            }
            const frameState = myState[frameKey]
            if( !frameState || typeof frameState !== "object"){
                return
            }
            if( !Array.isArray(frameState.flowActiveScopeIds) || frameState.flowActiveScopeIds.length === 0){
                return
            }
            if( frameState.flowActiveScopeIds.some(id=>changed.has(id))){
                const builder = frameState.flowActiveBuilder
                if( typeof builder === "function"){
                    canvas.current.updateActiveChip(frameState.id, !!builder())
                }
            }
        })
    }

    window.exportFrames = exportMultiple

    useDataEvent("relationship_update set_parameter set_field delete_primitive set_title new_child delete_primitive", undefined, (ids, event, info, fromRemote)=>{
        if( ids.length === 1 && ids[0] === primitive.id && typeof(info) == "string"){
            const frameUpdate = info.match(/frames\.(.+)\.(.+)/)
            if( frameUpdate && frameUpdate[2] === "showItems"){
                ids = [frameUpdate[1]]
            }
        }

        if( event === "new_child"){
            if( ids[0] === primitive.id ){
                const child = info?.child
                if( child){
                    if( !myState[child.id] && child.type !== "chat"){
                        addBoardToCanvas( child )
                    }
                }
            }
            return false
        }else if( event === "delete_primitive"){
            if( ids ){
                for(const id of ids){
                    canvas.current.removeFrame( id )
                    clearWatchList(id)
                }
            }
        }else if( event === "set_field" && info === "rationale"){
            return false
        }
        if(
            event === "set_field" &&
            typeof(info) === "string" &&
            (info.includes(".imageGallery.skipSourceIds") || info.startsWith("instanceConfig."))
        ){
            const changedIds = Array.isArray(ids) ? ids : []
            if( changedIds.length > 0 ){
                const impactedFrames = new Set()
                for(const changedId of changedIds){
                    Object.values(myState).forEach((entry)=>{
                        if( !entry || typeof entry !== "object" || !entry.isBoard ){
                            return
                        }
                        const primitiveId = entry.primitive?.id
                        const underlyingId = entry.underlying?.id
                        const watchesChangedId = Array.isArray(entry.internalWatchIds) && entry.internalWatchIds.includes(changedId)
                        if( primitiveId === changedId || underlyingId === changedId || watchesChangedId ){
                            impactedFrames.add(entry.id)
                        }
                    })
                }
                if( impactedFrames.size > 0 ){
                    const refreshIds = new Set()
                    for(const targetFrameId of impactedFrames){
                        const targetEntry = myState[targetFrameId]
                        if( !targetEntry?.primitive ){
                            continue
                        }
                        if( targetEntry.primitive.type === "page" ){
                            targetEntry.subpages = {}
                        }
                        refreshIds.add(targetFrameId)
                        const rebuildTargets = prepareBoard(targetEntry.primitive)
                        ;[rebuildTargets].flat().filter(Boolean).forEach((id)=>refreshIds.add(id))
                    }
                    refreshIds.forEach((targetFrameId)=>{
                        const board = myState[targetFrameId]
                        if( board?.primitive && canvas.current?.refreshFrame ){
                            canvas.current.refreshFrame(targetFrameId, renderView(board.primitive))
                        }
                    })
                    forceUpdateLinks()
                    return false
                }
            }
        }
        if( myState.current.watchList  ){

            myState.current.framesToUpdate = myState.current.framesToUpdate || []
            myState.current.framesToUpdateForRemote = myState.current.framesToUpdateForRemote || []
            log.trace(`Got data event`,{ids, event, info, fromRemote})
            Object.keys(myState.current.watchList).forEach(frameId=>{
                let listName = fromRemote ? "framesToUpdateForRemote" : "framesToUpdate"
                let timerName = fromRemote ? "frameUpdateTimerForRemote" : "frameUpdateTimer"
                let checkIds = ids
                if( myState[frameId] && myState.current.watchList[frameId].filter(d=>checkIds.includes(d)).length > 0 ){
                    
                    const existing = myState.current[listName].find(d=>d.frameId === frameId && d.event === event) 
                    if( !existing){
                        myState.current[listName].push({frameId, event, info})
                    }
                    
                    if( !myState.current[timerName] ){
                        myState.current[timerName] = setTimeout(()=>{
                            myState.current[timerName] = undefined
                            for( let {frameId, event, info} of  myState.current[listName]){
                                if( !myState[frameId] ){
                                    continue
                                }
                                log.trace(`In data event handler ${listName}`,{frameId, event, info})
                                let needRefresh = true
                                let refreshBoards = []
                                let resized = false
                                let changedRenderConfig = false
                                let needRebuild = ((event === "set_field" || event === "set_parameter") && info === "referenceParameters.explore.view")
                                //console.log(event, info)
                                

                                if( event === "set_field" && info && typeof(info)==="string"){
                                    if( info.includes('processing.mark_categories')){
                                        updateCategoryIndicators(ids)
                                        updateActiveChips(ids)
                                        needRefresh = false
                                        needRebuild = false
                                        continue
                                    }else if( info.startsWith('processing.flow') ){
                                        // Flow-instance lifecycle update: cheap chip update only.
                                        updateActiveChips(ids)
                                        needRefresh = false
                                        needRebuild = false
                                        continue
                                    }else if( info.startsWith('processing.run_step') ){
                                        // Step lifecycle on an external/action card — update chip
                                        // on any watching flow, and let the frame refresh below so
                                        // the card itself re-renders with/without its corner badge.
                                        updateActiveChips(ids)
                                    }else if( info.startsWith('processing.ai.')){
                                        const board = myState[frameId]
                                        canvas.current.refreshFrame( board.id, renderView(board.primitive))
                                    }else if(info.startsWith('frames.') && info.endsWith('.showItems')){
                                        needRebuild = true
                                        let targetFrame = info.match(/frames\.(.+)\..+/)?.[1]
                                        if(  targetFrame ){
                                            frameId =  targetFrame
                                        }
                                    }else if(info.startsWith('frames.') ){
                                        if((info.endsWith('.height') || info.endsWith('.width'))){
                                            resized = true
                                            needRebuild = true
                                        }else{
                                            needRefresh = true
                                            //needRebuild = true
                                        }
                                        if( mainstore.primitive(ids[0])?.type === "page"){
                                            myState[frameId].subpages = {}
                                            resized = true
                                        }
                                    }else if(info.startsWith('slide_state') ){
                                        needRefresh = true
                                        needRebuild = true
                                        changedRenderConfig  = true
                                    }else if(info.startsWith('renderConfig') ){
                                        needRefresh = true
                                        needRebuild = true
                                        changedRenderConfig  = true
                                    }else if(info.includes('.imageGallery.skipSourceIds') || info.startsWith('instanceConfig.')){
                                        needRefresh = true
                                        needRebuild = true
                                        const changedPrimitive = mainstore.primitive(ids?.[0])
                                        if( changedPrimitive?.type === "element" && myState[frameId]?.primitive?.type === "page"){
                                            myState[frameId].subpages = {}
                                        }
                                    }else if(
                                        info.startsWith('referenceParameters.explore.axis') ||
                                        info.startsWith('referenceParameters.explore.filters') ||
                                        info.startsWith('referenceParameters.datasetView')
                                    ){
                                        needRefresh = true
                                        needRebuild = true
                                    }else if(info.startsWith('processing.flow') ){
                                        needRefresh = true
                                    }else if(info.startsWith('processing.') || info.startsWith('embed_')){
                                        needRefresh = false
                                    }else if(info.startsWith("referenceParameters.inputPins.")  || info.startsWith("referenceParameters.outputPins.")){
                                        
                                        const state = myState[frameId]
                                        const basePrimitive = state.primitive
                                        const primitiveToPrepare = state.underlying ?? basePrimitive
                                        if( basePrimitive && primitiveToPrepare ){
                                            preparePins( primitiveToPrepare, basePrimitive, frameId, myState)
                                            
                                            needRefresh = false
                                            needRebuild = true
                                            resized = true
                                        }
                                    }
                                }
                                log.trace(` Post field check`,{needRebuild, needRefresh})
                                if(event === "set_field" || event === "set_parameter" ){
                                    const board = myState[frameId]
                                    if( board.primitive.type === "element"){
                                        needRebuild = true
                                    }
                                }
                                let refreshLinks = false
                                if( event === "relationship_update" || needRebuild){
                                    const framePrimitive = myState[frameId].primitive
                                    let doFrame = true
                                    if( framePrimitive.type === "page"){
                                        if( info === "renderConfig.theme"){
                                            needRefresh = prepareBoard( framePrimitive )
                                            const elements = framePrimitive.primitives.origin.allElement
                                            const theme = myState[framePrimitive.id].theme
                                            elements.forEach(d=>myState[d.id].theme = theme)
                                            if( needRefresh ){
                                                needRefresh = [...needRefresh, ...elements.map(d=>d.id)]
                                            }
                                            doFrame = false
                                        }else{
                                            let dIds = ids.filter(d=>d !== frameId)
                                            if(typeof(info) === "string" && info.startsWith('frames.') && (info.endsWith('.height') || info.endsWith('.width'))){
                                                dIds = [...dIds, info.split(".")[1]]
                                            }
                                            
                                            dIds.map(d=>myState[d]).forEach(other=>{
                                                if( other && other.primitive.type === "element"){
                                                    needRefresh = prepareBoard( other.primitive )
                                                    if( needRefresh ){
                                                        refreshBoards.push( other.id )
                                                        needRebuild = true
                                                    }
                                                    doFrame = false
                                                }
                                            })
                                        }
                                    }else{
                                        needRebuild = true
                                    }
                                    if( myState[framePrimitive.id]){
                                        refreshLinks = true
                                    }

                                    if( doFrame ){
                                        needRefresh = prepareBoard( framePrimitive )
                                        if( changedRenderConfig ){
                                            needRefresh = true
                                        }
                                    }
                                    if(resized ){
                                        needRefresh = true
                                    }
                                    if( !needRefresh){
                                        console.log(`Cancelled refresh - no changes on ${myState[frameId]?.primitive.plainId}`)
                                    }
                                }
                                log.trace(` Post handling`,{needRebuild, needRefresh})

                                if( needRefresh){
                                    forceUpdateLinks()
                                    
                                    refreshBoards = [...refreshBoards, ...(Array.isArray(needRefresh) ? needRefresh : [frameId])].filter((d,i,a)=>a.indexOf(d) === i)
                                    log.trace(` Refresh list`,{refreshBoards})

                                    console.log(`DOING REFRESH ${frameId} / ${myState[frameId]?.primitive.plainId}, [${refreshBoards.join(", ")}]`)

                                    if( needRebuild ){
                                        for(const frameId of refreshBoards ){
                                            const board = myState[frameId]
                                            canvas.current.refreshFrame( frameId, renderView(board.primitive))
                                        }
                                    }else{
                                        for(const frameId of refreshBoards ){
                                            canvas.current.refreshFrame( frameId )
                                        }
                                    }
                                }else{
                                    if( refreshLinks ){
                                        forceUpdateLinks()
                                    }
                                }
                            }
                            myState.current[listName] = []
                        }, fromRemote ? 4820 : 50)
                    }
                }
            })
        }
        return false
    })

    const list = primitive.primitives.allUniqueView

    const createView = async( action = {} )=>{
        if(!action?.key){
            console.error("NOT IMPLEMENETED")
            return
        }
        setManualInputPrompt({
            primitive: primitive,
            fields: action.actionFields,
            confirm: async (inputs)=>{
            const actionOptions = {
                ...inputs
            }
            console.log(action.key , actionOptions)
            await MainStore().doPrimitiveAction(primitive, action.key , actionOptions)
            },
        })
    }

    function updateWatchList(frameId, ids){
        myState.current.watchList = myState.current.watchList || {}
        myState.current.watchList[frameId] = [frameId, myState[frameId].underlying?.id,...(myState[frameId].internalWatchIds ?? [] ),...ids].filter(d=>d)
    }

    function clearWatchList(frameId){
        if( !frameId || !myState.current?.watchList ){
            return;
        }
        delete myState.current.watchList[frameId];
        if( Object.keys(myState.current.watchList).length === 0 ){
            delete myState.current.watchList;
        }
    }

    const prepareBoard = (d)=>SharedPrepareBoard(d, myState)

    useEffect(() => {
        const overlay = menu.current;
    
        const preventDefault = (e) => {
            if (e.ctrlKey) {
                e.preventDefault();
            }
        };
    
        overlay.addEventListener('wheel', preventDefault, { passive: false });
    
        return () => {
          overlay.removeEventListener('wheel', preventDefault);
        };
      }, [menu.current]);
    

    const action = primitive.metadata?.actions?.find(d=>d.key === "build_generic_view")


    async function cloneBoard(){
        if(myState.activeBoard){
            const p = myState.activeBoard.primitive
            if( p.type === "view" || p.type === "query"){
                console.log("adding")
                const newPrimitive = await mainstore.createPrimitive({
                    parent: p.origin, 
                    title: `Copy of ${p.title}`,
                    type: p.type,
                    workspaceId: p.workspaceId, 
                    categoryId: p.referenceId, 
                    referenceParameters: p.referenceParameters
                })
                
                if(newPrimitive ){
                    for(const imp of p.primitives.imports.allItems){
                        await newPrimitive.addRelationshipAndWait(imp, "imports")
                    }
                    for(const imp of p.primitives.axis.row.allItems){
                        await newPrimitive.addRelationshipAndWait(imp, "axis.row")
                    }
                    for(const imp of p.primitives.axis.column.allItems){
                        await newPrimitive.addRelationshipAndWait(imp, "axis.column")
                    }
                }        

                let position = canvas.current.framePosition(p.id)?.scene
                console.log("now to canvas")
                addBoardToCanvas( newPrimitive, {x:position.l, y: position.b + 30, s: position.s})
                console.log("done")
            }
            if( p.type === "element"){
                console.log("adding")
                const newPrimitive = await mainstore.createPrimitive({
                    parent: p.origin, 
                    title: `Copy of ${p.title}`,
                    type: p.type,
                    workspaceId: p.workspaceId, 
                    categoryId: p.referenceId, 
                    referenceParameters: p.referenceParameters
                })
                let position = canvas.current.framePosition(p.id)?.relative
                console.log("now to canvas")
                addBoardToCanvas( newPrimitive, {x:position.r + 10, y: position.t, s: position.s})
                console.log("done")
            }
        }
    }



    const [boards,  renderedSet] = useMemo(()=>{
        log.info(`--- REDO BOARD RENDER ${primitive?.id}, ${update}`)
        const boards = [
                        primitive?.type === "flow" ? primitive : undefined,
                        ...primitive.primitives.origin.allUniqueView,
                        ...primitive.primitives.origin.allUniquePage, 
                        ...primitive.primitives.origin.allUniqueSummary,
                        ...primitive.primitives.origin.allUniqueCategorizer,
                        ...primitive.primitives.origin.allUniqueQuery,
                        ...primitive.primitives.origin.allUniqueSearch,
                        ...primitive.primitives.origin.allUniqueQuestion,
                        ...primitive.primitives.origin.allUniqueFlow,
                        ...primitive.primitives.origin.allUniqueFlowcontrol,
                        ...primitive.primitives.origin.allUniqueExternal,
                        ...primitive.primitives.origin.allUniqueActionrunner,
                        ...primitive.primitives.origin.allUniqueAction].filter(Boolean)
        
        for(const d of boards){
            if(!myState[d.id] ){
                myState[d.id] = {id: d.id}
                prepareBoard(d)
            }
        }
        const allBoards = Object.values(myState).filter(d=>d && d.isBoard).map(d=>d.primitive).filter(Boolean)
        const renderedSet = allBoards.map(d=>renderView(d))
        return [allBoards, renderedSet]
    }, [primitive?.id, update])
        

    const linkList = useMemo(()=>{
        const start = performance.now()
        let links = []
        if( USE_LEGACY_ROUTING ){
            links = buildLegacyRoutingLinks(boards, myState)
        }else{
            links = buildSimpleRoutingLinks(boards, myState)
        }

        console.log(`Links (${USE_LEGACY_ROUTING ? "legacy" : "basic"}) took`, performance.now()-start)

        let oldLinks = canvas.current.getLinks ? canvas.current.getLinks() :undefined
        if( !mainstore.deepEqual( oldLinks, links) ){
            if( canvas.current?.updateLinks){
                canvas.current.updateLinks(links)
            }
        }else{
            console.log(`LINKS DID HNOT CAHANGE`)
        }
        return links
    }, [primitive?.id, update, updateLinks])

    let selectedColIdx = 0
    let selectedRowIdx = 0
    async function updateAxis(axisName, axis){
        if( myState?.activeBoard ){
            hideMenu()
            await CollectionUtils.setPrimitiveAxis(myState?.activeBoard.primitive, axis, axisName)
            prepareBoard( myState?.activeBoard.primitive )
            forceUpdateLinks()
            canvas.current.refreshFrame( myState.activeBoardId )
        }
    }
    async function updateExtents(board){
        const isActive = myState?.activeBoardId === board.id
        if( isActive ){
            hideMenu()
        }
        
        prepareBoard( board )
        forceUpdateLinks()

        if( isActive ){
            canvas.current.refreshFrame( myState.activeBoardId )
        }
    }

    let boardUpdateTimer

    function resizeFrame(fId, {x,y,width, height}, resizeInfo){
        let target = myState[fId].parentRender ? myState[myState[fId].parentRender].primitive : primitive
        const board = myState[fId]
        const debugEnabled = isHeightDebugEnabled() || board?.primitive?.referenceId === (PrimitiveConfig.Constants.TIMELINE_EVENT_SUMMARY ?? 178)
        if( debugEnabled ){
            console.debug("[height-debug][BoardViewer][resizeFrame:input]", {
                frameId: fId,
                incoming: {x,y,width,height},
                resizeInfo,
                viewConfig: myState[fId]?.config,
                primitiveRef: board?.primitive?.referenceId
            })
        }

        if( resizeInfo ){
            if( width !== undefined){
                if( resizeInfo.legendDeltaX ){
                    width -= resizeInfo.legendDeltaX
                }
                if( resizeInfo.widthPadding ){
                    width -= resizeInfo.widthPadding
                }else{
                    if( resizeInfo.padding){
                        width -= resizeInfo.padding[1] + resizeInfo.padding[3] 
                    }
                    width = (width - ((resizeInfo.columns - 1) * (resizeInfo.spacing?.[1] ?? 0))) 
                }
                if( resizeInfo.columns){
                    width = width / resizeInfo.columns
                }
            }
            if(height !== undefined){
                if( resizeInfo.legendDeltaY  ){
                    height -= resizeInfo.legendDeltaY
                }
                if( resizeInfo.padding){
                    height -= resizeInfo.padding[0] + resizeInfo.padding[2] 
                }
                if( resizeInfo.rows ){
                    height = (height - ((resizeInfo.rows - 1) * (resizeInfo.spacing?.[0] ?? 0))) / resizeInfo.rows
                }
            }
        }


        const updateData = {
            ...(target.frames?.[fId] ?? {}),
            ...(x !== undefined && { x }),
            ...(y !== undefined && { y }),
            ...(width !== undefined && width >0 && { width }),
            ...(height !== undefined && height >0 && { height }),
        }
        if( debugEnabled ){
            console.debug("[height-debug][BoardViewer][resizeFrame:normalized]", {
                frameId: fId,
                normalized: updateData,
                targetPrimitiveId: target?.id
            })
        }

        target.setField(`frames.${fId}`, updateData)
        canvas.current.updateFramePosition( fId, updateData)
        
        canvas.current.refreshFrame( board.id, renderView(board.primitive))
    }
    async function createElementFromActivePin(){
        const pin = myState.activePin
        if( pin ){
            const frame = myState[pin.frameId]
            const parent = frame.primitive
            const pinDef = frame.primitive.referenceParameters?.inputPins[pin.name]
            if( pinDef ){
                console.log(pinDef)
                const newPrim = await MainStore().createPrimitive({
                    title: `Element for ${parent.title}`,
                    type: "element",
                    parent: parent,
                    parentPath: `outputs.${pin.name}_impin`,
                    workspaceId: primitive.workspaceId
                })
                if( newPrim ){
                    await mainstore.waitForPrimitive( newPrim.id )
                    await newPrim.addRelationshipAndWait( parent, "imports")
                    const position = canvas.current.framePosition(frame.id).scene
                    addBoardToCanvas( newPrim, position)
                }
            }
        }
    }
    function disconnectActivePin(){
        if( myState.activePin ){
            let clearList
            const primitiveForPin = mainstore.primitive(myState.activePin.frameId)
            if( myState.activePin.name === "impin"){
                clearList = []
                primitiveForPin.primitives.imports.uniqueAllItems.map(d=>{
                    Object.keys(d.primitives.outputs ?? {}).filter(d=>d.endsWith(`_impin`)).map(r=>{
                        if( d.primitives.outputs[r].includes(primitiveForPin.id)){
                            clearList.push({
                                    parent: d,
                                    target: primitiveForPin,
                                    rel: `outputs.${r}`
                                })
                        }
                    })
                    clearList.push({
                            parent: primitiveForPin,
                            target: d,
                            rel: "imports"
                        })
                })
            }else{
                if( myState.activePin.output){
                    clearList = Object.keys(primitiveForPin._parentPrimitives ?? {}).map(d=>{
                        let matches = primitiveForPin._parentPrimitives[d].map(d=>d === `primitives.imports` ||  d.startsWith(`primitives.outputs.${myState.activePin.name}_`) || d.startsWith(`primitives.inputs.${myState.activePin.name}_`) ? d : undefined).filter(d=>d)
                        
                        if(matches.length > 0){
                            const src = mainstore.primitive(d)
                            if( src.type === "segment" && src.originId === primitiveForPin.id){
                                return
                            }

                            if( matches.includes("primitives.imports")){
                                console.log(`Checking imports for named import`)
                                const named = primitiveForPin._parentPrimitives[d].filter(d=>d.endsWith(`_impin`))
                                console.log(`Got ${named.length}`, named.join(", "))
                                const filtered = named.filter(d=>!matches.includes(d))
                                console.log(`Got ${filtered.length} filtered`, filtered.join(", "))
                                if( filtered.length > 0){
                                    console.log(`Preserving main import for outstanding named import`)
                                    matches = matches.filter(d=>d !== "primitives.import")
                                }
                            }

                            const set = matches.map(rel=>{
                                return {
                                    parent: src,
                                    target: primitiveForPin,
                                    rel: rel.slice(11)
                                }
                            })
                            console.log(set)
                            return set
                        }
                    }).flat(Infinity).filter(d=>d)
                }else{
                    clearList = [
                        ...Object.keys(primitiveForPin.primitives.inputs ?? {}).filter(d=>d.endsWith(`_${myState.activePin.name}`)).map(r=>{
                            return primitiveForPin.primitives.inputs[r].uniqueAllItems.map(d=>{
                                return {
                                    parent: primitiveForPin,
                                    target: d,
                                    rel: "inputs." + r
                                }
                            })}),
                        ...Object.keys(primitiveForPin.primitives.outputs ?? {}).filter(d=>d.endsWith(`_${myState.activePin.name}`)).map(r=>{
                            return primitiveForPin.primitives.outputs[r].uniqueAllItems.map(d=>{
                                return {
                                    parent: primitiveForPin,
                                    target: d,
                                    rel: "outputs." + r
                                }
                            })}),
                    ].flat(Infinity).filter(d=>d )
                }
            }
            if( clearList ){
                for(const d of clearList){
                    d.parent.removeRelationship(d.target, d.rel)
                    console.log(`Remove relation ${d.rel} from parent ${d.parent.plainId} to ${d.target.plainId}`)
                }
            }
        }
    }

    function setActivePin(pin){
        myState.activePin = pin
        const frame = myState[pin.frameId]
        myState.createFromActivePin = frame.primitive.type === "page" && frame.primitive.referenceParameters?.inputPins?.[pin.name]
        {
            setActiveBoard( [pin.frameId] )
        }
    }

    function setActiveBoard(e){
        const id = [e].flat()[0]
        myState.activeBoardId = id
        if( id !== myState.activePin?.frameId){
            myState.activePin = undefined
        }
        if( id ){
            myState.activeBoard = myState[id]
            if(true || !myState[id].axisOptions ){
                const source = myState[id].underlying ?? myState[id].primitive
                myState[id].axisOptions = CollectionUtils.axisFromCollection( myState[id].primitiveList ? myState[id].primitiveList : source.itemsForProcessing, source,  source.referenceParameters?.explore?.hideNull)
                //myState[id].axisOptions = CollectionUtils.axisFromCollection( source.itemsForProcessing, source,  source.referenceParameters?.explore?.hideNull)
            }

            let addToView = false
            if( myState.activeBoard.primitive.type === "view" ){
                if( myState.activeBoard.primitive.primitives.imports.allIds.length === 0){
                    addToView = {view: myState.activeBoard.primitive, segment: undefined}
                }else{
                    const target = myState.activeBoard.primitive.primitives.imports.allItems.find(d=>d.type === "search" || (d.type === "segment" && d.primitives.imports.allIds.length === 0))
                    if( target ){
                        addToView = {view: myState.activeBoard.primitive, segment: target}
                    }
                }
            }

            myState.menuOptions = {
                showAddChild: myState.activeBoard.primitive.type !== "element" && myState.activeBoard.primitive.type !== "page",
                showAxis: myState.activeBoard.config !== "widget" && myState.activeBoard.primitive.type !== "element" && myState.activeBoard.primitive.type !== "page",
                showClone: myState.activeBoard.type === "query" || myState.activeBoard.primitive.type === "view" || myState.activeBoard.primitive.type === "element",
                addToView
            }

            handleViewChange(true)

            const parentRenderUnderlying = myState.activeBoard.parentRender ? myState[myState.activeBoard.parentRender]?.underlying : undefined
            
            setCollectionPaneInfo({
                frame: myState.activeBoard.primitive, 
                underlying: myState.activeBoard.underlying, 
                axisSource: myState.activeBoard.axisSource,
                parentPageInstance: parentRenderUnderlying?.type === "page" ? parentRenderUnderlying : undefined,
                flowInstances: myState.activeBoard.flowInstances, 
                inFlowInstance: myState.activeBoard.flowInstance, 
                board: primitive,
                localItems: getLocalPrimitiveListForBoardId(id, myState),
                originalList: myState[id].originalList
            })
        }else{
            if( primitive?.type ==="flow" ){
                setCollectionPaneInfo({
                    frame: primitive, 
                    flowInstances: myState.mainFlowInstances, 
                    inFlowInstance: undefined, 
                    board: primitive,
                    //localItems: []                    
                })
            }
            myState.activeBoard = undefined
            myState.menuOptions = {}
            hideMenu()
        }
        if( agentRef.current ){
            const prim = myState.activeBoard?.underlying ?? myState.activeBoard?.primitive
            if( prim?.type === "page" && false){
                agentRef.current.setContext( uniquePrimitives(myState.activeBoard.primitive.primitives.allElement.flatMap(d=>myState[d.id].axisSource).filter(Boolean)) )
            }else{
                agentRef.current.setContext( prim )
            }

        }
    }

    function menuSide(){
        return myState.menuSide
    }

    function getAxisId(axis){
        if( !myState?.activeBoard ){
            return undefined
        }
        return CollectionUtils.findAxisItem( myState?.activeBoard.primitive, axis, myState?.activeBoard.axisOptions )
    }
    function getAxisOptions(){
        return myState?.activeBoard?.axisOptions ?? []
    }

    function updateMenuPosition(boardScreenPosition){
        if(myState.activeBoard && menu.current){
            const vSize = canvas.current.size()
            const buffer = 80
            const offset = 10
            const roomOnLeft = boardScreenPosition.l > buffer
            const roomOnRight = boardScreenPosition.r < (vSize[0] - buffer)

            if( roomOnLeft ){
                menu.current.style.left = parseInt( boardScreenPosition.l - buffer + offset) + "px"
                myState.menuSide = boardScreenPosition.l > 400 ? "left" : "right"
            }else if( roomOnRight ){
                menu.current.style.left = parseInt( boardScreenPosition.r + offset) + "px"
                myState.menuSide = "left"
            }else{
                menu.current.style.left = offset + "px"
                myState.menuSide = "right"
            }

            const menuHeight = menu.current.offsetHeight

            let tc =Math.max(boardScreenPosition.t, 0)
            let bc =Math.min(boardScreenPosition.b, vSize[1])
            let top = (((bc - tc) / 2) - (menuHeight / 2)) + tc
            if( top < 0){
                top = offset
            }else if((top + menuHeight) + buffer > vSize[1]){
                top = vSize[1] - offset - menuHeight
            }
            menu.current.style.top = top + "px"
        }

    }
    function hideMenu(){
        if( menu.current ){
            menu.current.style.visibility = "hidden"
        }
    }
    function handleViewWillChange(e){
        hideMenu()
    }
    function handleViewChange(instant = false){
        if( canvas.current ){
            if(myState.activeBoard){
                if( boardUpdateTimer ){
                    clearTimeout( boardUpdateTimer )
                }
                boardUpdateTimer = setTimeout(()=>{
                    updateMenuPosition(canvas.current.framePosition(myState.activeBoardId)?.viewport )
                    if( menu.current ){
                        menu.current.style.visibility = "unset"
                        rowButton.current?.refocus && rowButton.current?.refocus()
                        colButton.current?.refocus && colButton.current?.refocus()
                    }
                }, instant ? 0 : 250)
            }
        }
    }

    function addBoardToCanvas( d, position ){
        if( !position){
            position = findSpace()
        }
        let updatePrimitive = primitive
        myState[d.id] = {id: d.id, primitive: d}
        if( d.type === "element"){
            myState[d.id].inPage = true
            myState[d.id].page = myState[d.origin.id]
            myState[d.id].parentRender = d.origin.id
            updatePrimitive = d.origin
        }else if(d.flowElement){
            myState[d.id].parentRender = d.origin.id
            updatePrimitive = d.origin
        }
        prepareBoard( d )

        if( position ){
            updatePrimitive.setField(`frames.${d.id}`, {x: position.x, y: position.y, s: position.s})
            canvas.current.updateFramePosition( d.id,  {x: position.x, y: position.y, s: position.s})
        }

        canvas.current.addFrame( renderView(d))
        forceUpdate()
    }

    function addExistingView(){
        let items = mainstore.primitives().filter(d=>d.workspaceId === primitive.workspaceId && ["working","view","query","search"].includes(d.type))

        const activeBoardIds = Object.keys(myState)
        items = items.filter(d=>!activeBoardIds.includes(d.id))

        mainstore.globalPicker({
            list: items,
            callback: (d)=>{
                primitive.addRelationship(d, "ref")
                let position = canvas.current.framePosition(myState.activeBoardId)?.scene ?? {r: 0, t: 0, s: 1}
                addBoardToCanvas( d, {x: position.r +50, y: position.t, s: position.s})
                return true
            }

        })
    }
    async function addWidgetChildView(){
        if(myState.activeBoard){
            const bp = myState.activeBoard.primitive
            if( bp.type === "search"){
                const resultCategoryIds = bp.metadata.parameters.sources.options.map(d=>d.resultCategoryId).filter((d,i,a)=>a.indexOf(d) === i)
                if( resultCategoryIds.length > 0 ){
                    await addBlankView( undefined, bp.id, undefined, {referenceId: resultCategoryIds})
                }
                
            }else if( bp.type === "query"){
                let resultCategoryIds = bp.itemsForProcessing.map(d=>d.referenceId).filter((d,i,a)=>a.indexOf(d)===i)
                if( resultCategoryIds.length === 0){
                    if( bp.referenceParameters.extract ){
                        resultCategoryIds = [bp.referenceParameters.extract]
                    }else{
                        resultCategoryIds = [82]
                    }
                }
                if( resultCategoryIds.length > 0 ){
                    await addBlankView( undefined, bp.id, undefined, {referenceId: resultCategoryIds})
                }
                
            }else if( bp.type === "action" || bp.type === "actionrunner" || bp.type === "categorizer"){
                let resultCategoryIds = bp.itemsForProcessing.map(d=>d.referenceId).filter((d,i,a)=>a.indexOf(d)===i).filter(d=>["entity","result","evidence"].includes(mainstore.category(d).primitiveType))
                if( resultCategoryIds.length > 0 ){
                    await addBlankView( undefined, bp.id, undefined, {referenceId: resultCategoryIds})
                }
            }
        }
    }
    function pickBoardDescendant(){
        if(myState.activeBoard){
            let importedSet = myState.activeBoard.primitive.importedBy.map(d=>[d.primitives.origin.allUniqueQuery, d.primitives.origin.allUniqueView]).flat(Infinity)
            let summaries = [...myState.activeBoard.primitive.primitives.origin.allUniqueSummary, ...myState.activeBoard.primitive.primitives.origin.allUniqueSegment.map(d=>d.primitives.allUniqueSummary).flat(), ...myState.activeBoard.primitive.primitives.origin.allUniqueSegment.map(d=>d.primitives.allUniqueQuery).flat()]
            let items = [...summaries,...myState.activeBoard.primitive.primitives.origin.allUniqueQuery, ...myState.activeBoard.primitive.primitives.origin.allUniqueView, ...importedSet]

            const activeBoardIds = Object.keys(myState)
            items = items.filter(d=>!activeBoardIds.includes(d.id))

            mainstore.globalPicker({
                list: items,
                callback: (d)=>{
                    primitive.addRelationship(d, "ref")
                    let position = canvas.current.framePosition(myState.activeBoardId)?.scene
                    addBoardToCanvas( d, {x:position.r +50, y: position.t, s: position.s})
                }

            })

        }        
    }
    function removeBoard(){
        if(myState.activeBoard){
            let title = "Remove from board?"
            let action = ()=>primitive.removeRelationship(myState.activeBoard.primitive, "ref")

            if( myState.activeBoard.primitive.origin.id === primitive.id){
                title = `Delete ${myState.activeBoard.primitive.displayType}?`
                action = ()=>mainstore.removePrimitive( myState.activeBoard.primitive )

            }
            mainstore.promptDelete({
                title: "Confirmation",
                prompt: title,
                handleDelete: ()=>{
                    action()                        
                    hideMenu()
                    canvas.current.removeFrame( myState.activeBoard.id )
                    clearWatchList(myState.activeBoard.id)
                    delete myState[myState.activeBoard.id]
                    myState.activeBoard = undefined
                    setCollectionPaneInfo(undefined)
                    forceUpdate()
                    return true
                }
            })
        }
    }

    window.removeActiveBoard = removeBoard


    function renderView(d, options = {}){
        return SharedRenderView(d, primitive, myState, options)
    }
    async function addBlankView(cat_or_id = 38, importId, filter, full_options = {}){
        const category = typeof(cat_or_id) === "number" ? mainstore.category(cat_or_id) : cat_or_id
        const {pivot, ...options} = full_options 
        let importPrimitive = importId ? mainstore.primitive(importId) : undefined
        let position = (importPrimitive ? canvas.current.framePosition(importPrimitive.id)?.scene : undefined) ?? {r:0, t: 0, s: 1}
        let createInFlow = myState[importId]?.inFlow ? myState[importId]?.flow : false
        
        if(pivot){
            console.log("has pivot", pivot)
            const existingSegments = importPrimitive.primitives.allUniqueSegment
            console.log(`Got ${existingSegments.length} segments to check`)
            let targetTargetSegment = existingSegments.find(d=>d.doesImport(importId, filter) && mainstore.equalRelationships(pivot, d.referenceParameters?.pivot))
            console.log(targetTargetSegment)
            if(!targetTargetSegment){
                const newPrim = await mainstore.createPrimitive({type: 'segment', 
                    parent: importPrimitive, 
                    referenceParameters:{
                        target:"items",
                        pivot,
                        importConfig: [{id: importPrimitive.id, filters: filter}]
                    }})
                if( newPrim ){
                    await mainstore.waitForPrimitive( newPrim.id )
                    await newPrim.addRelationshipAndWait( importPrimitive, "imports")
                    console.log(`create new segment ${newPrim.id}`)
                    targetTargetSegment = newPrim
                }
            }
            if( !targetTargetSegment ){
                console.warn(`Cannot create interim segment for pivot view`)
                return
            }
            importPrimitive = targetTargetSegment
            filter = undefined
        }
        const newPrimitive = await mainstore.createPrimitive({
            title: `New ${category.primitiveType}`,
            categoryId: category.id,
            type: category.primitiveType,
            flowElement: createInFlow !== false,
            referenceParameters: {
                ...(importPrimitive ? {target: "items", importConfig: [{id: importPrimitive.id, filters: filter}]} : {}),
                target: "items",
                ...options,
            },
            parent: createInFlow ? createInFlow : primitive,
        })
        if( newPrimitive ){
            if(importPrimitive){
                await newPrimitive.addRelationshipAndWait( importPrimitive, "imports")
            }
            if( !createInFlow ){
                primitive.addRelationship(newPrimitive, "ref")
                addBoardToCanvas( newPrimitive, {x:position.r + 50, y: position.t, s: position.s})
            }

        }
    }

    function newView(referenceCategoryId){
        let items = mainstore.primitives().filter(d=>d.workspaceId === primitive.workspaceId && ["activity"].includes(d.type))
            
        mainstore.globalPicker({
            list: items,
            callback: (d)=>{

                mainstore.globalNewPrimitive({
                    title: "New view",
                    type: ["view", "query"],
                    originTask: d,
                    parent: primitive,
                    callback:(d)=>{
                        addBoardToCanvas( d )
                        return true
                    }
                })
            }

        })

    }
    async function createNewQuery( parent, data ){

        const addAsChild = parent.type === "query"
        console.log(data)

        const queryData = data?.target ?? data
        const importData = data?.importConfig 


        await mainstore.doPrimitiveAction( parent, "new_query", {queryData, importData},async (result)=>{
            if( result ){
                if( result.flow ){
                    console.log(`Need to refresh flow for new query`)
                    const flow = MainStore().primitive(result.flow)
                    prepareBoard( myState[flow.id].primitive )
                    canvas.current.refreshFrame( flow.id, renderView(flow))

                }else{
                    const newPrimitive = await MainStore().waitForPrimitive( result.primitiveId )
                    let position = canvas.current.framePosition(parent.id)?.scene
                    await primitive.addRelationshipAndWait(newPrimitive, "ref")
                    addBoardToCanvas( newPrimitive, {x:position.r +50, y: position.t, s: position.s})
                }
            }
        })
    }
    function findSpace(){
        let position = {x:0, y:0, s:1}
        if( myState.activeBoard){
            let current = canvas.current.framePosition(myState.activeBoardId)?.scene
            if( current ){
                position = {x:current.l, y: current.b + 30, s: current.s}
            }
        }else{
            let all = canvas.current.framePosition()
            if( all.length > 0){
                let minX, maxY 
                for(const d of all){
                    if( d.id !== primitive.id){
                        if(minX == undefined || d.scene.x < minX){
                            minX = d.scene.x
                        }
                        if(maxY == undefined || d.scene.b > maxY){
                            maxY = d.scene.b
                        }
                    }
                }
                position.x = minX  ?? 0 
                position.y = (maxY ?? 0) + 30
            }
        }
        return position
    }
    async function getOrCreateSuitableView(item){
        const manual = primitive.primitives.manual.allUniqueSegment[0]
        if(!manual){
            console.log(`Can't find maual`)
            return
        }
        const views = primitive.primitives.origin.allUniqueView.filter(d=>{
            if(d.doesImport(manual.id)){
                if( d.referenceParameters?.referenceId === item.referenceId){
                    return true
                }
                if( Array.isArray(d.referenceParameters?.referenceId) && d.referenceParameters.referenceId.includes(item.referenceId)){
                    return true
                }
            } 
            return false
        })
        if( views.length > 0){
            console.log(`Got view`)
            return views[0]
        }
        console.log(`Need to create vew`)
        const result = await MainStore().createPrimitive({
            title: item.metadata?.plural ?? item.metadata?.title ?? "View",
            type: "view",
            parent: primitive,
            parentPath: "origin",
            workspaceId: primitive.workspaceId,
            "referenceParameters": {
                "referenceId": item.referenceId
            }
        })
        let view = await mainstore.waitForPrimitive( result.id )
        if( view ){
            await primitive.addRelationshipAndWait(view, "ref")
            await view.addRelationshipAndWait(manual, "imports")
            addBoardToCanvas( view, findSpace())
        }

    }
    window.getTest = getOrCreateSuitableView

    function addToView(){
        if( myState.menuOptions?.addToView ){
            const config = myState.menuOptions.addToView
            const target = config.segment ?? config.view
            const categoryList = [config.view.referenceParameters.referenceId].flat().filter(d=>d)
            pickNewItem( {categoryList, addTo: config})

        }
    }

    function pickNewItem(options = {}){
        const addToFlow = (myState.activeBoard && myState.activeBoard.primitive?.type === "flow") ? myState.activeBoard.primitive : undefined
        const addToPage = (myState.activeBoard && myState.activeBoard.primitive?.type === "page") ? myState.activeBoard.primitive : undefined


        let {categoryList, addTo} = options

       if(!categoryList  ){
           if( addToPage ){
               const defaultElementId = PrimitiveConfig.typeConfig?.element?.defaultReferenceId ?? 89
               categoryList = [
                    defaultElementId,
                    PrimitiveConfig.Constants.SHAPE_ELEMENT,
                    PrimitiveConfig.Constants.IMAGE_ELEMENT
               ]
            }else{
                categoryList = [
                    38, 81, 148, 154, 130, 140, 131, 142, 118, 135, 136, 137, 132, 133, 144, 162, 183,
                    ...mainstore.categories().filter(d=>d.primitiveType === "search").map(d=>d.id),
                    ...(addToFlow ? [81,113,183] : mainstore.categories().filter(d=>d.primitiveType === "entity").map(d=>d.id)),
                ].flat()
            }
        }

        mainstore.globalNewPrimitive({
            title: addTo ? `Add to ${addTo.view.title} (#${addTo.view.plainId})` : (addToFlow ? `Add to ${addToFlow.title} flow` : "Add to board"),
            categoryId: categoryList,
            parent: primitive,
            beforeCreate:async (data)=>{
                if( addToFlow ){
                    return {
                        ...data,
                        flowElement: true,
                        parent: addToFlow
                    }
                }else if( addToPage ){
                    return {
                        ...data,
                        parent: addToPage
                    }
                }else if( addTo ){
                    console.log(addTo)
                    let targetSegment = addTo.segment
                    if( !targetSegment){
                        console.log(`Need to source create segment for view`)
                        const result = await MainStore().createPrimitive({
                            title: `Segment for View ${addTo.view.plainId}`,
                            type: "segment",
                            parent: primitive,
                            workspaceId: primitive.workspaceId
                        })
                        if( result ){
                            targetSegment = await mainstore.waitForPrimitive( result.id )
                            await addTo.view.addRelationshipAndWait( targetSegment, "imports")
                        }
                    }
                    if( !targetSegment ){
                        return false
                    }
                    return {
                        ...data,
                        parent: targetSegment
                    }
                }else{
                    if( !addTo ){
                        if( data.type === "entity" || data.type === "result"){
                            console.log(`Entity selected - need to add to manual segment`)
                            let segment = primitive.primitives.manual.allUniqueSegment[0]
                            if( !segment ){
                                console.log(`Manual doesnt exist - creating`)
                                const result = await MainStore().createPrimitive({
                                    title: `Manual segment for Board ${primitive.plainId}`,
                                    type: "segment",
                                    parent: primitive,
                                    parentPath: "manual",
                                    workspaceId: primitive.workspaceId
                                })
                                segment = await mainstore.waitForPrimitive( result.id )
                            }
                            if( segment ){
                                console.log(`Got manual segment`)
                                return {
                                    ...data,
                                    parent: segment
                                }
                            }
                            throw "Cant add to board - couldnt find manual segment"
                        }
                    }
                }
                return data
            },
            callback:async (d)=>{
                if( d ){
                    if( addToFlow ){
                        addBoardToCanvas( d, {x:0, y:0, s:1})
                    }else if( addToPage ){
                        addBoardToCanvas( d, {x:0, y:0, s:1})
                    }else{
                        if(d.type === "entity" || d.type === "result"){
                            await getOrCreateSuitableView(d)
                        }
                        addBoardToCanvas( d, findSpace())
                    }
                    return true
                }
            }
        })
    }

    async function handleDropNewPrimitive(e){
        if( !canvas.current ){
            return
        }
        const categoryIdValue = e.dataTransfer.getData('application/x-category') || e.dataTransfer.getData('text/plain')
        if(!categoryIdValue){
            return
        }
        const categoryId = Number(categoryIdValue)
        if(Number.isNaN(categoryId) || !allowedCategoryIdSet.has(categoryId)){
            return
        }
        const dropTarget = document.elementFromPoint(e.clientX, e.clientY)
        if(dropTarget?.closest('[data-cancel-drop]')){
            return
        }
        e.preventDefault()
        setShowAddDrawer(false)


        let [sceneX, sceneY] = canvas.current.convertClientCoords( {x: e.clientX, y: e.clientY})
        
        const addToFlow = (myState.activeBoard && myState.activeBoard.primitive?.type === "flow") ? myState.activeBoard.primitive : undefined
        const addToPage = (myState.activeBoard && myState.activeBoard.primitive?.type === "page") ? myState.activeBoard.primitive : undefined

        if( addToFlow || addToPage ){
            const {l, t} = canvas.current.framePosition( myState.activeBoard.id )?.scene
            sceneX -= l
            sceneY -= t
        }

        const cat = mainstore.category(categoryId)
        if(cat){
            const referenceParameters = {}
            if( categoryId === PrimitiveConfig.Constants.SHAPE_ELEMENT){
                referenceParameters.style = "text"
                referenceParameters.text = "New shape"
                referenceParameters.fontSize = 30
            }
            const newPrim = await mainstore.createPrimitive({
                title: cat.title,
                type: cat.primitiveType,
                categoryId: cat.id,
                parent: primitive,
                workspaceId: primitive.workspaceId,
                ...(addToFlow ? {flowElement: true, parent: addToFlow} : {}),
                ...(addToPage ? {parent: addToPage} : {}),
                referenceParameters
            })
            if(newPrim){
                addBoardToCanvas(newPrim, {x: sceneX, y: sceneY, s:1})
            }
        }
    }
    async function exportMultiple(ids, byCell){
        const prims = ids.map(d=>mainstore.primitive(d)).filter(d=>d)
        const pptx = createPptx()
        for( const d of prims ){
            const root = canvas.current.frameData( d.id )?.node
            if( root ){
                let list 
                let labels = []
                if( byCell ){
                    list = root.find('.cell')
                    const len = Math.max(...list.map(d=>d.children?.length ?? 0))
                    if( len === 1){
                        labels = list.map(d=>{
                            const [col, row] = d.attrs.id.split("-")
                            return root.find('.row_header').find(d=>d.attrs.id===row)?.find('CustomText')[0]?.attrs?.text
                        })
                        list = root.find('.primitive')
                    }
                }
                let idx = 0
                for(const d of list){
                    await exportKonvaToPptx( d, pptx, {title: labels ? labels[idx]  : undefined} )
                    idx ++
                }
            }
        }
        writePptx( pptx)
    }
    async function exportReport(asTable = false){
        if(myState.activeBoard){
                const frames = canvas.current.getSelection("frame")
                const pptx = createPptx({width: 8.5, height: 11})

                const pxToInch = 612 / 8.5

                let bounds = [
                    11 / 811 * 72,
                    8.5 / 612 * 552,
                    11 / 811 * 739,
                    8.5 / 612 * 60,
                ]//.map(d=>d.toFixed(3))

                pptx.defineSlideMaster({
                    title: "MASTER_SLIDE",
                    background: { color: "FFFFFF" },
                    objects: [
                     { line: { x: bounds[3], y: bounds[0], w: bounds[1] - bounds[3], h: 0, line: { color: "D4D4D4", width: 1 } } },
                     { line: { x: bounds[3], y: bounds[2], w: bounds[1] - bounds[3], h: 0, line: { color: "D4D4D4", width: 1 } } },
                     { text:  {
                        text: 'COMPANY ANALYSIS - SENSE',
                        options: { x: bounds[3], y: bounds[0], w:'100%', align:'left', color:'000000', fontSize:8, bold: true, valign: "bottom",margin:[0,0,0,0] }
                    }},
                     { text:  {
                        text: "Note: This report includes content generated by artificial intelligence (AI). While accuracy is a priority, it is recommended to use this information as a supplement to, rather than a sole basis for, decision-making.",
                        options: { x: bounds[3], y: bounds[2], w: bounds[1] - bounds[3], align:'left', color:'555555', fontSize:7, valign: "top",margin:[5,0,0,0] }
                    }}
                    // { line: { x: bounds[3], y: bounds[2], w: bounds[3] - bounds[1], line: { color: "0088CC", width: 5 } } },
                    ],
                    slideNumber: { x: 500 / pxToInch , w: 52 / pxToInch , y: 11 / 811 * 742, fontSize: 8, bold: true, align: 'right',margin:[0,0,0,0]} ,
                   });

                for(const d of frames){
                    const root = canvas.current.frameData( d.attrs.id )
                    const temp = root.node.children
                    root.node.children = root.allNodes

                    const padding = [bounds[0],0,bounds[0], 0]

                    await exportKonvaToPptx( root.node, pptx, {offsetForFrame: [root.canvasMargin[3], root.canvasMargin[0]], master: "MASTER_SLIDE", removeNodes:IGNORE_NODES_FOR_EXPORT,  scale: 1 / pxToInch / root.node.attrs.scaleX, padding} )
                    root.node.children = temp
                }
                pptx.writeFile({ fileName: "Konva_Stage_Export.pptx" });
        }
    }
    async function exportFrame(asTable = false, byCell, options = {}){
        if(myState.activeBoard){
            canvas.current.clearHighlights()
            if( asTable ){
                const root = canvas.current.frameData( myState.activeBoardId )
                const temp = root.node.children
                root.node.children = root.allNodes
                await exportKonvaToPptx( root.node, mainstore.keepPPTX, {removeNodes: IGNORE_NODES_FOR_EXPORT, fit:"width", asTable: true, padding: [3, 1, 0.25, 1]} )
                root.node.children = temp
                if( !mainstore.disablePPTXSave ){
                    mainstore.keepPPTX?.writeFile({ fileName: "Konva_Stage_Export.pptx" });
                }
            }else if(byCell){
                const frames = canvas.current.getSelection("frame")
                const pptx = createPptx()
                for(const d of frames){
                    const root = canvas.current.frameData( d.attrs.id )
                    const temp = root.node.children
                    root.node.children = root.allNodes

                    const cells = root.node.find('.cell')
                    for(const cell of cells){
                        canvas.current.restoreChildren( cell, root.node )
                        await exportKonvaToPptx( cell, pptx, {removeNodes: IGNORE_NODES_FOR_EXPORT,  padding: [0,0,0,0]} )
                        canvas.current.restoreChildrenForTracking( cell )
                    }

                    root.node.children = temp
                }
                pptx.writeFile({ fileName: "Konva_Stage_Export.pptx" });

            }else{
                const frames = options.allFrames ? canvas.current.frameList() : canvas.current.getSelection("frame")
                const pptx = createPptx()
                for(const d of frames){                    
                    const childFrames = Object.values(myState).filter(d2=>d2?.parentRender === d.attrs.id).map(d=>canvas.current.frameData(d.id)).filter(d=>d)
                    const page = d.find(".page")[0]
                    const background = page?.find(".background")[0]
                    const backgroundColor = background?.attrs.fill
                    
                    if( childFrames.length > 0){
                        const aggNode = new Konva.Group({
                            x: d.x(),
                            y: d.y(),
                            width: d.width() * d.scaleX(),
                            height: d.height() * d.scaleY()
                        })
                        for(const root of childFrames){
                            root.temp = root.node.children
                            root.node.x( root.x - d.x() )
                            root.node.y( root.y - d.y() )
                            root.node.children = root.allNodes
                            root.oldParent = root.node.parent
                            aggNode.add( root.node )
                        }
                        await exportKonvaToPptx( aggNode, pptx, {removeNodes: IGNORE_NODES_FOR_EXPORT,   noFraming: true, padding: [0,0,0,0], backgroundColor} )
                        for(const root of childFrames){
                            root.node.x( root.x )
                            root.node.y( root.y )
                            root.oldParent.add( root.node )
                            root.node.children = root.temp
                            delete root["temp"]
                            delete root["oldParent"]
                        }

                    }else{
                        const root = canvas.current.frameData( d.attrs.id )
                        let pages = root.node.find("._page")
                        const temp = root.node.children
                        root.node.children = root.allNodes

                        if( pages.length > 0){
                            for(const page of pages){
                                const background = page.find(".background")[0]
                                const backgroundColor = background?.attrs.fill
                                const childFrames = root.node.find(d=>d.attrs.pageTrack === page.attrs.pageIdx)
                                const aggNode = new Konva.Group({
                                    width: page.width(),
                                    height: page.height()
                                })
                                for(const child of childFrames){
                                    child.ox = child.x()
                                    child.oy = child.y()
                                    child.x( child.ox - page.x() )
                                    child.y( child.oy - page.y() )
                                    child.oldParent = child.parent
                                    aggNode.add( child )
                                }
                                await exportKonvaToPptx( aggNode, pptx, {removeNodes: IGNORE_NODES_FOR_EXPORT,  noFraming: true, padding: [0,0,0,0], backgroundColor} )
                                for(const child of childFrames){
                                    child.x( child.ox )
                                    child.y( child.oy )
                                    child.oldParent.add( child )
                                    delete child["oldParent"]
                                    delete child["ox"]
                                    delete child["oy"]
                                }
                            }
                        }else{
                            await exportKonvaToPptx( root.node, pptx, {removeNodes: IGNORE_NODES_FOR_EXPORT,  padding: [3, 1, 0.25, 1]} )
                        }
                        
                        root.node.children = temp
                    }
                }
                pptx.writeFile({ fileName: "Konva_Stage_Export.pptx" });
            }
        }else{
            await exportKonvaToPptx( canvas.current.stageNode() )
        }

    }
    async function copyToClipboard(){
        if(myState.activeBoard){
            const view = myState[myState.activeBoard.id]
            if( view.referenceId === 118){
                const root = mainstore.primitive(view.order?.[0])
                const rootList = root.itemsForProcessing


            }
            const type = view.list[0]?.primitive.type
            let out = "~" + view.columns.map(d=>d.label).join("~")
            
            out += "\n" + view.rows.map(row=>{
                return row.label +"~" + view.columns.map(column=>{
                    const subList = view.list.filter(d=>[d.column].flat().includes( column.idx) && [d.row].flat().includes( row.idx))
                    let text = "-"
                    if( subList.length > 0){
                        if( type === "evidence"){
                            //text = subList.map(d=>([d.primitive.title, d.primitive.referenceParameters?.quote].filter(d=>d).join("\n")).replaceAll(/\n/g, "-ENT-")).join("-ENT-")
                            text = subList.length > 0 ? "✓" : ""
                        }  else{
                            text = subList.map(d=>(d.primitive.referenceParameters?.summary ?? d.primitive.referenceParameters?.description ?? "").replaceAll(/\n/g, "-ENT-")).join("-ENT-")
                        }                 
                    }
                    const partial = `"${text}"`
                    return partial
                }).join("~")
            }).join("\n")
            navigator.clipboard.writeText( out )
        }

    }
    function frameActionCallback(primitive, action){
        if( action == "capture_sizing"){
            if( primitive.type === "element"){
                const page = primitive.origin                
                if( page.type === "page"){
                    let position = canvas.current.framePosition(primitive.id)
                    if( position.scene ){
                        page.setField(`frames.${primitive.id}.placeholder.sizing`, {width: position.scene.width, height: position.scene.height})
                    }                    
                }
            }

        }
    }

    function newDescendView(){
        if(myState.activeBoard){
            const addAsChild = myState.activeBoard.primitive.type === "query"
            mainstore.globalNewPrimitive({
                title: "New view",
                type: ["view", "query"],
                originTask: myState.activeBoard.primitive,
                parent: addAsChild ? myState.activeBoard.primitive : primitive,
                callback:(d)=>{
                    let position = canvas.current.framePosition(myState.activeBoardId)?.scene
                    if( addAsChild ){
                        primitive.addRelationship(d, "ref")
                    }
                    addBoardToCanvas( d, {x:position.r + 50, y: position.t, s: position.s})
                    return true
                }
            })
        }
    }

    const flowChildPositions = Object.values(myState).filter(d=>d && d.isBoard && d.primitive?.type === "flow").reduce((a,d)=>({...a,...d.primitive.frames}),{})
    const pageChildPositions = Object.values(myState).filter(d=>d && d.isBoard && d.primitive?.type === "page").reduce((a,d)=>({...a,...d.primitive.frames}),{})
    
    let framePositions = {
        ...primitive.frames,
        ...flowChildPositions,
        ...pageChildPositions
    }

    function checkPinConnect(sourceId, sourcePin, targetId, targetPin){
        if( sourceId === targetId ){
            return false
        }
        const sourcePrimitive = mainstore.primitive(sourceId)
        const targetPrimitive = mainstore.primitive(targetId)
        if( sourcePrimitive && targetPrimitive){
            const isInternalFlowPin = (sourcePrimitive.inFlow && targetPrimitive.type === "flow") && sourcePrimitive.parentPrimitiveIds.includes(targetPrimitive.id)
            let targetIsPageElement 
            if( targetPrimitive.type === "element"){
                const topElement = targetPrimitive.configParent ?? targetPrimitive
                if( topElement.origin.flowElement ){
                    targetIsPageElement = true
                }
            }
            if( !targetIsPageElement && sourcePrimitive.inFlow && !targetPrimitive.inFlow && !isInternalFlowPin){
                return false
            }
            const inputPins = isInternalFlowPin ? targetPrimitive.outputPins : targetPrimitive.inputPins
            const canConnect = PrimitiveConfig.canConnect({
                input: {
                    config: inputPins,
                    pin: targetPin
                },
                output:{
                    config: sourcePrimitive.outputPins,
                    pin: sourcePin
                }

            })
            console.log(canConnect)
            return {result: canConnect, isInternalFlowPin, sourcePrimitive, targetPrimitive}
        }
        return false
    }

    const dockPaneInfo = true
    const collectPaneWidget = collectionPaneInfo ? <CollectionInfoPane {...collectionPaneInfo} frameAction={frameActionCallback} newPrimitiveCallback={createNewQuery} createNewView={addBlankView} updateFrameExtents={updateExtents}/> : undefined

    const agentScope = {
        constrainTo: primitive.id,
        activeFlowInstanceId: primitive.type === "flow" ? myState.mainFlowInstance?.id : undefined
    }

    return <div className="flex w-full h-full bg-gray-100 dark:bg-slate-950 overflow-hidden">
        {manualInputPrompt && <InputPopup key='input' cancel={()=>setManualInputPrompt(false)} {...manualInputPrompt}/>}
            {dockPaneInfo &&  <div className='overflow-y-scroll shrink-0 bg-white dark:bg-slate-950 w-[24rem] xl:w-[32rem] 6xl:w-[40rem] py-3 px-3 flex-col flex border-r dark:border-slate-800'>
                <Tabs fullWidth variant="solid" selectedKey={panelTab} onSelectionChange={((id)=>setPanelTab(id))}>
                    <Tab key="info" title="Inspector"/>
                    <Tab key="ai" title="SENSE AI"/>
                </Tabs>
                {collectionPaneInfo && panelTab === "info" && collectPaneWidget}
                {<div key='chatbar' className={clsx([
                            'flex flex-col overflow-hidden p-3 place-items-start text-sm grow',
                            panelTab === "ai" ? "" : "hidden",
                            "h-full w-full",
                        ])}>
                            {!agentStatus.hasReplies && <div className="w-full flex justify-center space-x-2 place-items-center grow h-full">
                                <div className="bg-gray-50 rounded-xl border p-6 m-4 text-slate-700 w-72">
                                    SENSE AI can help you setup this workflow.  Tell it what you're looking to do...
                                </div>
                            </div>}
                            <AgentChat 
                                    mode={primitive.type === "flow" ? "flow_editor" : "board"} 
                                    externalContext={mainstore.activeBoard?.primitive}
                                    setStatus={setAgentStatus} 
                                    ref={agentRef}
                                    scope={agentScope}
                                    primitive={primitive} 
                                    seperateInput={true}/>
                        </div>
                    }
            </div>}
            <div className="flex relative w-full h-full @container overflow-clip" onDrop={handleDropNewPrimitive} onDragOver={(e)=>e.preventDefault()}>
                {!dockPaneInfo && <div key='chatbar' className={clsx([
                    'absolute bg-white border border-gray-200 bottom-4 space-y-2 flex flex-col left-4 overflow-hidden p-3 place-items-start rounded-md shadow-lg text-sm z-50 ',
                    agentStatus.activeChat ? 'max-h-[80vh] w-[40vw] 4xl:max-w-3xl min-w-[24rem]' : "w-96 max-h-[80vh]"
                ])}>
                    <AgentChat 
                        ref={agentRef}
                        mode={primitive.type === "flow" ? "flow_editor" : "board"}
                        setStatus={setAgentStatus} 
                        contextClick={(d)=>mainstore.sidebarSelect(d)}
                        primitive={primitive}/>
                </div>}
                
                <div className='absolute right-4 top-4 z-40 flex flex-col items-end gap-3 pointer-events-none'>
                    <div key='toolbar3' className='overflow-hidden max-h-[80vh] bg-white rounded-md shadow-lg border-gray-200 border flex flex-col place-items-start divide-y divide-gray-200 pointer-events-auto'>
                        <div className='p-3 flex place-items-start space-x-2 '>
                                <DropdownButton noBorder icon={<HeroIcon icon='FAPickView' className='w-6 h-6 mr-1.5'/>} onClick={addExistingView} flat placement='left-start' />
                                <DropdownButton noBorder icon={<PlusIcon className='w-6 h-6 mr-1.5'/>} onClick={()=>setShowAddDrawer(!showAddDrawer)} flat placement='left-start' />
                                <DropdownButton noBorder icon={<HeroIcon icon='FAAddView' className='w-6 h-6 mr-1.5'/>} onClick={newView} flat placement='left-start' />
                                {collectionPaneInfo && <DropdownButton noBorder icon={<HeroIcon icon='FAAddChildNode' className='w-6 h-6 mr-1.5'/>} onClick={pickBoardDescendant} flat placement='left-start' />}
                                {false && <DropdownButton noBorder icon={<DocumentArrowDownIcon className='w-6 h-6 mr-1.5'/>} onClick={()=>exportFrame(false,true)} flat placement='left-start' />}
                                {<DropdownButton noBorder icon={<DocumentArrowDownIcon className='w-6 h-6 mr-1.5'/>} onClick={()=>exportFrame(false)} flat placement='left-start' />}
                                {false && <DropdownButton noBorder icon={<DocumentArrowDownIcon className='w-6 h-6 mr-1.5'/>} onClick={()=>exportReport(false)} flat placement='left-start' />}
                                {collectionPaneInfo && <DropdownButton noBorder icon={<ClipboardDocumentIcon className='w-6 h-6 mr-1.5'/>} onClick={copyToClipboard} flat placement='left-start' />}
                        </div>
                        {!dockPaneInfo && collectionPaneInfo && <div className='pt-2 overflow-y-scroll'>
                            <div className='w-[32rem] 3xl:w-[40rem]'>
                                {collectPaneWidget}
                            </div>
                        </div>}
                    </div>
                    <PrimitiveDrawer
                        open={showAddDrawer}
                        onClose={()=>setShowAddDrawer(false)}
                        className='pointer-events-auto mt-1 max-h-[calc(100vh_-_20rem)]'
                        categories={allowedDrawerCategories}
                    />
                </div>
                {<div ref={menu} key='toolbar' className='bg-white rounded-md shadow-lg border-gray-200 border absolute z-40 p-1.5 flex flex-col place-items-start space-y-2 invisible'>
                    {myState.menuOptions?.addToView && <DropdownButton noBorder icon={<PlusIcon className='w-5 h-5'/>} onClick={addToView} flat placement='left-start' />}
                    {myState.menuOptions?.showAxis && <HierarchyNavigator ref={colButton} noBorder align={()=>menuSide()} icon={<HeroIcon icon='Columns' className='w-5 h-5 '/>} items={()=>CollectionUtils.axisToHierarchy(getAxisOptions())} flat placement='left-start' portal showTick selectedItemId={()=>getAxisId("column")} action={(d)=>updateAxis("column", d)} dropdownWidth='w-64' className={`hover:text-ccgreen-800 hover:shadow-md ${selectedColIdx > 0 ? "!bg-ccgreen-100 !text-ccgreen-700" : ""}`}/>}
                    {myState.menuOptions?.showAxis && <HierarchyNavigator ref={rowButton} noBorder align={()=>menuSide()} icon={<HeroIcon icon='Rows' className='w-5 h-5 '/>} items={()=>CollectionUtils.axisToHierarchy(getAxisOptions())} flat placement='left-start' portal showTick selectedItemId={()=>getAxisId("row")} action={(d)=>updateAxis("row", d)} dropdownWidth='w-64' className={`hover:text-ccgreen-800 hover:shadow-md ${selectedRowIdx > 0 ? "!bg-ccgreen-100 !text-ccgreen-700" : ""}`}/>}
                    {myState.menuOptions?.showAddChild && <DropdownButton noBorder icon={<HeroIcon icon='FAAddChildNode' className='w-5 h-5'/>} onClick={addWidgetChildView} flat placement='left-start' />}
                    {myState.activeBoard && <DropdownButton noBorder icon={<HeroIcon icon='FAClearRectangle' className='w-5 h-5'/>} onClick={removeBoard} flat placement='left-start' />}
                    {myState.activeBoard && myState.menuOptions?.showClone  && <DropdownButton noBorder icon={<HeroIcon icon='FACloneRectangle' className='w-5 h-5'/>} onClick={cloneBoard} flat placement='left-start' />}
                    {myState.activePin && <DropdownButton noBorder icon={<HeroIcon icon='FALinkBreak' className='w-5 h-5'/>} onClick={disconnectActivePin} flat placement='left-start' />}
                    {myState.createFromActivePin && <DropdownButton noBorder icon={<HeroIcon icon='FAAddChildNode' className='w-5 h-5'/>} onClick={createElementFromActivePin} flat placement='left-start' />}
                </div>}
                <div className={`w-full flex min-h-[40vh] h-full rounded-md`} style={{background:"#f1f1f3"}}>
                    <InfiniteCanvas 
                                    primitive={primitive}
                                    board
                                    background="#f1f1f3"
                                    ref={setCanvasRef}
                                    ignoreAfterDrag={true}
                                    highlights={{
                                        "frame": "glow",
                                        "primitive":"border",
                                        "cell":"background",
                                        "pin":"background",
                                        "widget":"background"
                                    }}
                                    rerender={(frame, primitiveId)=>{
                                        const prim = MainStore().primitive(primitiveId)
                                        return RenderPrimitiveAsKonva( primitive)
                                    }}
                                    enableFrameSelection
                                    updateWatchList={updateWatchList}
                                    drag={{
                                        pin:{
                                            pin: {
                                                drop: (sourceId, sourcePin, targetId, targetPin)=>{
                                                    const canConnect = checkPinConnect( sourceId, sourcePin, targetId, targetPin )
                                                    if( canConnect){
                                                        const baseRel = canConnect.isInternalFlowPin ? "outputs" : "inputs"
                                                        if( targetPin === "impin"){
                                                            console.log(`Will connect target ${canConnect.targetPrimitive.plainId} to source ${canConnect.sourcePrimitive.plainId} as import`)
                                                            canConnect.targetPrimitive.addRelationship( canConnect.sourcePrimitive, "imports")
                                                            if( sourcePin !== "impout"){
                                                                const rel = `outputs.${sourcePin}_${targetPin}`
                                                                console.log(`Will connect target ${canConnect.sourcePrimitive.plainId} to source ${canConnect.targetPrimitive.plainId} at ${rel}`)
                                                                canConnect.sourcePrimitive.addRelationship( canConnect.targetPrimitive, rel)
                                                            }
                                                        }else{
                                                            const rel = `${baseRel}.${sourcePin}_${targetPin}`
                                                            canConnect.targetPrimitive.addRelationship( canConnect.sourcePrimitive, rel)
                                                            console.log(`Will connect target ${canConnect.targetPrimitive.plainId} to source ${canConnect.sourcePrimitive.plainId} at ${rel}`)
                                                        }
                                                        return true
                                                    }
                                                },
                                                droppable: (sourceId, sourcePin, targetId, targetPin)=>{
                                                    return checkPinConnect(sourceId, sourcePin, targetId, targetPin) !== false
                                                }
                                            }
                                        },
                                        "primitive": {
                                            start: (id, frameId)=>{
                                                const framePrimitive = mainstore.primitive( frameId )
                                                if( framePrimitive.type === "element"){
                                                    return "frame_parent"
                                                }
                                            },
                                            cell:{
                                                start: undefined,
                                                droppable: (id,start, drop, sFrame, dFrame)=>{
                                                    if( sFrame !== dFrame ){
                                                        return false
                                                    }                                                
                                                    let frameId = sFrame
                                                    
                                                    const [sc,sr] = dropZoneToAxis(start)
                                                    const [dc,dr] = dropZoneToAxis(drop)
                                                    if( sr != dr && !myState[frameId].axis?.row?.allowMove){
                                                        return false
                                                    }
                                                    if( sc != dc && !myState[frameId].axis?.column?.allowMove){
                                                        return false
                                                    }
                                                    return true
                                                },
                                                drop: (id, start, drop, sFrame, dFrame)=>{
                                                    if( sFrame !== dFrame ){
                                                        return false
                                                    }                                                
                                                    let frameId = sFrame
                                                    moveItemWithinFrame(id, start, drop, myState[frameId])
                                                }
                                            }
                                        }
                                    }}
                                    callbacks={{
                                        resizeFrame,
                                        viewportWillMove:handleViewWillChange,
                                        viewportCallback:()=>handleViewChange(),
                                        frameMove: (d)=>{
                                            const prim = MainStore().primitive(d.id)
                                            if(prim){

                                                console.log(d)

                                                let target = primitive
                                                let scaledWidth, scaledHeight


                                                if( myState[d.id].parentRender ){
                                                    target = myState[myState[d.id].parentRender].primitive
                                                    console.log(`Will update position in flow parent`)
                                                }

                                                const updateData = {
                                                    ...(target.frames?.[d.id] ?? {}),
                                                    x: d.x, 
                                                    y: d.y, 
                                                    s: d.s
                                                }
                                                
                                                target.setField(`frames.${d.id}`, updateData)

                                                canvas.current.updateFramePosition( d.id, {x: updateData.x, y: updateData.y, s: updateData.s})
                                            }
                                        },
                                        onClick:{
                                            pin: (ids, frameId, {name, output})=>{
                                                console.log(frameId, name, output)
                                                setActivePin({frameId, name, output})
                                            },
                                            frame: (id)=>setActiveBoard(id),
                                            primitive:(id, frameId)=>{
                                                const frame = mainstore.primitive(frameId)
                                                if( myState.activeBoard?.id ===  frameId){
                                                    mainstore.sidebarSelect(id)
                                                }else{
                                                    setActiveBoard( frame.id )
                                                    if( canvas.current ){
                                                        canvas.current.selectFrame( frame.id )
                                                    }

                                                }
                                                if( frame?.type === "element"){
                                                    setActiveBoard( frame.id )
                                                    if( canvas.current ){
                                                        canvas.current.selectFrame( frame.id )
                                                    }
                                                }
                                            },
                                            canvas:(id)=>primitive?.type === "flow" ? setActiveBoard() : setCollectionPaneInfo(),
                                            toggle_items:(id, frameId, data)=>{
                                                let target = primitive
                                                if( myState[frameId].parentRender ){
                                                    target = myState[myState[frameId].parentRender].primitive
                                                    console.log(`Will update toggle in flow parent`)
                                                }
                                                target.setField(`frames.${frameId}.showItems`, !(data?.open ?? false))
                                            },
                                            page:(id, frameId, data, konvaNode)=>{
                                                setActiveBoard(frameId)
                                                if( canvas.current ){
                                                    canvas.current.selectFrame( frameId )
                                                }
                                            },
                                            action_primitive:(id, frameId, data, konvaNode)=>{
                                                setActiveBoard(frameId)
                                                if( canvas.current ){
                                                    canvas.current.selectFrame( frameId )
                                                }
                                            },
                                            column_header:(id, frameId, data, konvaNode)=>{
                                                setActiveBoard(frameId)
                                                if( canvas.current ){
                                                    canvas.current.selectFrame( frameId )
                                                }
                                            },
                                            row_header:(id, frameId, data, konvaNode)=>{
                                                setActiveBoard(frameId)
                                                if( canvas.current ){
                                                    canvas.current.selectFrame( frameId )
                                                }
                                            },
                                            cell:(id, frameId, data, konvaNode)=>{
                                                let cell = id?.[0]
                                                let nested 
                                                const cellAncestors = konvaNode.findAncestors('.cell')
                                                if( cellAncestors.length > 0 ){
                                                    const baseCell = cellAncestors.shift()
                                                    nested = cell
                                                    cell = baseCell.attrs.id
                                                    if( cellAncestors.length > 0 ){
                                                        console.warn(`Cant handle multiple nested cells`)
                                                    }
                                                }
                                                if( cell ){
                                                    let sourceState = myState[frameId]
                                                    if( sourceState.axisSource ){
                                                        let axisSource = sourceState.axisSource
                                                        if( axisSource.inFlow && axisSource.configParent.flowElement ){
                                                            axisSource = axisSource.configParent
                                                        }
                                                        sourceState = myState[axisSource.id]
                                                    }

                                                    let cIdx, rIdx, filters = []

                                                    if(myState[frameId].data){
                                                        const data = myState[frameId].data
                                                        const cellData = data.cells.find(d=>d.id === cell)
                                                        console.log(nested)
                                                        filters = [
                                                            PrimitiveConfig.encodeExploreFilter( data.defs?.columns, cellData.columnIdx ),
                                                            PrimitiveConfig.encodeExploreFilter( data.defs?.rows, cellData.rowIdx ),
                                                        ]
                                                        if( nested ){
                                                            const idx = parseInt(nested.split("-")[1])
                                                            filters.push(
                                                                PrimitiveConfig.encodeExploreFilter( data.defs.allocations?.[0], Object.values(cellData.allocations)[0]?.[idx]?.idx )
                                                            )
                                                        }
                                                    //}else if(myState[frameId].axis){
                                                    }else if(sourceState.axis){
                                                        [cIdx,rIdx] = cell.split("-")
                                                        filters = [
                                                            PrimitiveConfig.encodeExploreFilter( sourceState.axis.column, sourceState.columns[cIdx] ),
                                                            PrimitiveConfig.encodeExploreFilter( sourceState.axis.row, sourceState.rows[rIdx] ),
                                                        ]
                                                        
                                                    }

                                                    console.log(filters)
                                                    setCollectionPaneInfo({frame:  sourceState.underlying ??  sourceState.primitive, board: primitive, filters: filters.filter(d=>d)})
                                                    if( !myState.activeBoard || myState.activeBoard.id !== frameId){
                                                        setActiveBoard(frameId)
                                                        canvas.current.selectFrame( frameId )
                                                    }
                                                }else{
                                                    return false
                                                }
                                            },
                                            widget:{
                                                show_extra:(d,frameId)=>{
                                                    const cellId = d.attrs.id
                                                    console.log(`Toggle extra of ${frameId} / ${cellId}`)
                                                    let key
                                                    if( myState[frameId].data){
                                                        key = cellId
                                                    }else{
                                                        const [cIdx,rIdx] = cellId.split("-")
                                                        const mappedColumn = myState[frameId].columns[cIdx] 
                                                        const mappedRow = myState[frameId].rows[rIdx] 
                                                        key = [mappedColumn?.idx, mappedRow?.idx].filter(d=>d).join("-")
                                                    }
                                                    const current = primitive.frames?.[frameId]?.expand ?? {}

                                                    if( current[key] ){
                                                        delete current[key]
                                                    }else{
                                                        current[key] = true
                                                    }
                                                    console.log(key, current)
                                                    primitive.setField(`frames.${frameId}.expand`, current)
                                                    canvas.current.updateFramePosition( frameId, {expand: current})
                                                    canvas.current.refreshFrame( frameId)
                                                }
                                            }

                                        },
                                        onToggle:async (primitiveId, toggle, frameId)=>{
                                            console.log(`Will toggle ${toggle} on ${primitiveId} for frame ${frameId}`)
                                            if( toggle && primitiveId && myState[frameId]){
                                                const axisValue = myState[frameId].extents[toggle].filter(d=>d.idx !== "_N_")?.[0]
                                                const target = mainstore.primitive(primitiveId)
                                                const category = mainstore.primitive(axisValue.idx)
                                                if( target && category ){
                                                    let result 
                                                    const currentState = target.parentPrimitiveIds.includes(category.id)
                                                    if( currentState ){
                                                        await category.removeRelationship(target,"ref")
                                                        result = false
                                                    }else{
                                                        await category.addRelationship(target,"ref")
                                                        result = true
                                                    }

                                                    for(const targetBoard of boards){
                                                        if( targetBoard.id !== frameId){
                                                            prepareBoard( targetBoard )
                                                            canvas.current.refreshFrame( targetBoard.id )
                                                        }
                                                    }
                                                    return result
                                                    
                                                }
                                            }
                                        },
                                    }}
                                    frameLinks={linkList}
                                    framePositions={framePositions}
                                    selectable={{
                                        "frame":{
                                            multiple: true
                                        },
                                        "primitive":{
                                            multiple: false
                                        },
                                        "pin":{
                                            multiple: false
                                        },
                                        "cell":{
                                            multiple: true
                                        }
                                    }}
                                    render={renderedSet}
                        />
                    {false && <div className="flex flex-col w-[36rem] h-full justify-stretch space-y-1 grow border-l p-3">
                        <FilterPane/>
                    </div>}
                    
            </div>
        </div>
    </div>
}
BoardViewer.prepareBoard = SharedPrepareBoard
BoardViewer.renderBoardView = SharedRenderView
BoardViewer.getPageEmptyInfo = getPageEmptyInfo
